import moment from 'moment'
import React from 'react'
import { ValueOf } from 'type-fest'
import { z } from 'zod'

import { capitalize } from '@/lib/capitalize'
import { TFunction } from '@/lib/i18n/i18n'
import { Maybe } from '@/lib/types'
import { BillingCost, BillingType, FeatureScope, Money, PackageStatus, TripContentAllowance } from '@/types/billing'
import { CarLoyaltyProgram } from '@/types/car'
import {
	CountryNameZ,
	Currency,
	Identifiable,
	LocationTypes,
	OriginalAmount,
	PricesMetadata,
	ProductType,
	StatsBuckets,
	StatsBucketsPerItem,
	TripId,
	UserCurrency,
	UserId,
	WithCancellationStatus,
	WithName,
	WithUserId,
} from '@/types/common'
import { CompanyFeatureFlags, CompanyPreferenceType, CompanyType } from '@/types/company'
import { CreditCardResponse, CreditCardType, WithCardCvc, WithCreditCards } from '@/types/creditCard'
import { NationalId, Passport, TravelerDocumentType } from '@/types/document'
import { ErrorsInBooking } from '@/types/errors'
import { ExpenseReportResponse } from '@/types/expenses/report'
import {
	ExternalBookingFeedback,
	IHotelContentSource,
	PastBillingCost,
	SupportRequestFeeForm,
	Tax,
} from '@/types/externalBooking'
import {
	Airline,
	FlightBookingSegmentResId,
	FlightCarrierCode,
	FlightFareGroupKey,
	FlightIdentifier,
	FlightOptionKey,
	FlightProvider,
	FlightSearchSegmentId,
	FlightSearchSliceId,
	FlightSegmentId,
	FlightSegmentRefId,
	FlightSliceId,
	MissingFrequentFlyerInformation,
} from '@/types/flights/common'
import {
	FlightAmenity,
	FlightBaggageAllowance,
	FlightCarrier,
	FlightFareComponent,
	FlightPenaltiesArray,
	FlightPolicyDeviation,
} from '@/types/flights/search'
import { BookingOptionUTA } from '@/types/flights/uta'
import { Group, GroupTypes } from '@/types/group'
import { CompanyInvoiceStatus, InvoiceProfile } from '@/types/invoiceProfile'
import type { JobStatus } from '@/types/job'
import { CountriesCode, Locales } from '@/types/locale'
import { City, ExternalLocationId, Location, LocationId, LongitudeLatitudeBounds } from '@/types/location'
import { TripPaymentMethodBase, WithPaymentMethodId } from '@/types/payment'
import { POSCountry } from '@/types/pos'
import type { DeliveryOptions, RailReservationOffer } from '@/types/rails/booking'
import type {
	RailLegId,
	RailPassengerPricePointId,
	RailProviderName,
	RailReservationOfferId,
	RailSeatId,
	RailSeatPreferenceGroupId,
	RailSectionId,
	RailVendor,
} from '@/types/rails/common'
import type {
	RailSearchDetails as RailSearchDetailsNew,
	RailSearchId,
	RailSearchRequestDetails,
	RailTravelClass,
} from '@/types/rails/search'
import type { RailSeatPreferenceGroup, RailSelectedSeatPreference } from '@/types/rails/seatPreference'
import { SearchId, SearchJobStatus } from '@/types/search'
import { SeatMapFlightResultSelection, SeatMapSeat, SeatMapSegmentsSelection } from '@/types/seatMap'
import {
	BookingSegmentId,
	BookingSegmentToTraveler,
	BSTTId,
	ReservationStatus,
	TripDirection,
	TripStatus,
} from '@/types/trip'
import { Role, TravelProfile, User } from '@/types/user'
import { PersonNames } from '@/types/userNew'
import { VirtualPaymentAutomation, WithVirtualPaymentAutomations } from '@/types/virtualPaymentAutomation'

import {
	CarAdditionalDetailsName,
	CarAgencyLocation,
	CarCancellationPolicy,
	CarFee,
	CarFuel,
	CarRate,
	CarRateDescription,
	CarRentAgency,
	CarSpecialEquipment,
	CarTransmission,
	CarType,
	CarVendor,
	Mileage,
} from './cars.type'
import { FareComponent } from './fare-rules'
import { ReportingFields } from './report-settings.type'
import { SupportRequest } from './support-request'
import { WorldlineOrderStatus } from './worldline'

export const WEBSITE_BASE = 'getgoing.com'

export const LocaleToCurrency: Record<Locales, UserCurrency> = {
	[Locales.enUS]: UserCurrency.USD,
	[Locales.enGB]: UserCurrency.USD,
	[Locales.deDE]: UserCurrency.EUR,
	[Locales.frFR]: UserCurrency.EUR,
	[Locales.nlNL]: UserCurrency.EUR,
}

export const CARD_TYPES_WITHOUT_REQUIRED_CVV = [
	CreditCardType.AirPlus,
	CreditCardType.AmexBTA,
	CreditCardType.AmexBTAGermany,
]

// TODO: Check with BA
export const CVV_LENGTH_BY_CARD_TYPE = {
	[CreditCardType.Visa]: 3,
	[CreditCardType.MasterCard]: 3,
	[CreditCardType.AmericanExpress]: 4,
	[CreditCardType.Diners]: 3,
	[CreditCardType.AirPlus]: 3,
	[CreditCardType.AmexBTA]: 4,
	[CreditCardType.AmexBTAGermany]: 4,
}

export const CARD_NUMBER_LENGTH_BY_CARD_TYPE: { [key in CreditCardType]: number[] } = {
	[CreditCardType.Visa]: [16],
	[CreditCardType.MasterCard]: [16],
	[CreditCardType.Diners]: [14, 15, 16, 17, 18, 19],
	[CreditCardType.AirPlus]: [15, 16],
	[CreditCardType.AmexBTA]: [15],
	[CreditCardType.AmexBTAGermany]: [15],
	[CreditCardType.AmericanExpress]: [15],
	[CreditCardType.Other]: [],
}

// eslint-disable-next-line no-restricted-syntax
export enum LoginStatus {
	CompanyDoesNotExist = 1,
	UserDoesNotExist = 0,
	UserExists = 2,
	NotAcceptedTos = 3,
}

export interface LoginDetails {
	user: User
	login_status: LoginStatus
	is_impersonation: boolean
	user_can_operate: boolean
	cc_special_access?: boolean
}

export interface OrderVerificationData extends WithCardCvc<string | null> {
	worldline_validated: boolean
	trip_id?: number | null
}

export function getProductTypeLabel({ productType, t }: { productType: ProductType; t: TFunction }) {
	return {
		[ProductType.Flights]: t('trip-content-types.flights'),
		[ProductType.Hotels]: t('trip-content-types.hotels'),
		[ProductType.Cars]: t('trip-content-types.cars'),
		[ProductType.Rails]: t('trip-content-types.trains'),
	}[productType]
}

export interface SearchData<T = AnySearchDetails> extends WithTravelers {
	type: ProductType
	details: T
	related_search?: SearchData<RailSearchDetailsNew>
}

export interface SearchRequestWithTrain {
	type: ProductType
	details:
		| FlightSearchRequestDetails
		| HotelSearchRequestDetails
		| CarSearchRequestDetails
		| RailSearchDetails
		| (RailSearchRequestDetails & { search_group_id?: string })
	travelers_ids: UserId[]
	trip_id: TripId
	old_search_id?: SearchId
}

interface WithAirlineAlliances {
	airline_alliances: string[] | null
}

interface WithAirlines {
	airlines: Airline[] | null
}

export interface FlightSearchDetails extends WithAirlineAlliances, WithAirlines {
	routes: FlightSearchRouteDetails[]
	flight_type: FlightDirection
	cabin_class: CabinClassName
	max_num_of_connections: number
	trip_id?: number | null
	fully_flexible?: boolean
	refundable_only?: boolean
	changeable_only?: boolean
	included_baggage?: boolean
	baggage_only?: boolean
	users_to_rail_cards?: RailSearchDetailsNew['users_to_rail_cards']
	users_dob?: RailSearchDetailsNew['users_dob']
}

// eslint-disable-next-line no-restricted-syntax
export enum TimeRestrictionType {
	Arrival = 'arrival',
	Departure = 'departure',
}

export interface TimeRestriction {
	time: string
	time_type: TimeRestrictionType | null
	time_window: number
}

export interface WithFlightDepartureSettings {
	departure_date: string | null
	time_restriction: TimeRestriction | null
}

export interface FlightSearchRequestDetailsBase<T> extends WithAirlineAlliances {
	routes: T[]
	flight_type: FlightDirection
	cabin_class: CabinClassName
	max_num_of_connections: number
}

export type FlightSearchRequestDetails = FlightSearchRequestDetailsBase<FlightSearchRequestRouteDetails> & {
	airline_codes: string[] | null
}

export interface LocationDetails {
	location: Location
	location_type: LocationTypes
}

export interface FlightSearchRouteDetails extends WithFlightDepartureSettings {
	from_location: Location | null
	to_location: Location | null
	key?: string
	connecting_locations: LocationDetails[] | null
	from_city?: City
	to_city?: City
}

interface FlightSearchRequestRouteDetailsBase {
	from_location: LocationId | null
	to_location: LocationId | null
	from_location_type: LocationTypes | null
	to_location_type: LocationTypes | null
}

interface ConnectingLocationRequestDetails {
	location_id: LocationId
	location_type: LocationTypes
}

export type RailTravelersCardsAge = {
	[userId: number]: {
		cards: string[]
		rail_card_numbers?: Record<string, string>
		age?: number
	}
}

interface FlightSearchRequestRouteDetails extends FlightSearchRequestRouteDetailsBase, WithFlightDepartureSettings {
	timezone_offset: number
	connecting_locations: ConnectingLocationRequestDetails[]
}

export interface HotelSearchDetails {
	near_location: Location | null
	from_date: string | null
	until_date: string | null
	geo?: GeoSearchDetails
	term?: string
	radius: number | null
	radius_units: ShortDistanceUnits
	include_non_traditional_properties?: boolean
}

export interface HotelSearchRequestDetails {
	near_location_type: LocationTypes
	near_location: LocationId | null
	from_date: string | null
	until_date: string | null
	geo?: GeoSearchDetails
	term?: string
}

export interface CarSearchDetails {
	pickup_location: Location | null
	dropoff_location?: Location | null
	from_date: string | null
	until_date: string | null
}
export interface CarSearchRequestDetails {
	pickup_location: LocationId | null
	dropoff_location?: LocationId | null
	pickup_location_type?: LocationTypes | null
	dropoff_location_type?: LocationTypes | null
	from_date: string | null
	until_date: string | null
}

/** @deprecated Use the new one from types */
export interface RailSearchDetails {
	rail_type: FlightDirection
	departure_station: RailStationDetails
	arrival_station: RailStationDetails
	departure_time: string
	train_class: string
	return_time?: string
}

export interface RailStationDetails extends WithName {
	created_dt: string
	visibility: 'private' | 'public' | 'hidden'
	id: number
	lat: number
	long: number
	country: string | null
	airport_name: string
	address: string | null
	location_type?: LocationTypes.Rail
	short_name: string | null
	country_name: string | null
	number: number
}

export interface GeoSearchDetails {
	long: number
	lat: number
	radius: number
}

export type AnySearchDetails =
	| FlightSearchDetails
	| HotelSearchDetails
	| CarSearchDetails
	| RailSearchDetails
	| RailSearchDetailsNew

export type UserCreationPayload = Pick<
	User,
	'first_name' | 'last_name' | 'email' | 'middle_name' | 'invoice_profile' | 'image_url'
> & {
	phone_number?: string | null
	role?: Role | null
	group?: Group | null
	office_location?: Location | null
	language_code?: Locales | null
	invoice_profile_id?: InvoiceProfile['id'] | null
	deactivate_at?: Date | string | null
}

export interface UserWithPolicies extends User {
	ignore_approval: boolean
	custom_policy: {
		id: number
	} & WithName
}

// eslint-disable-next-line no-restricted-syntax
export enum ProfileType {
	Permanent = 'permanent',
	Temporary = 'temporary',
}

export interface GroupPurchasingOrganization extends WithName {
	id: number
}

export interface Company extends WithName {
	id: number
	logo_url: string | null
	flights_allowance: TripContentAllowance
	hotels_allowance: TripContentAllowance
	reports_enabled: boolean
	created_dt: string
	company_type: CompanyType
	billing_type: BillingType
	is_after_onboarding: boolean
	accepted_tos?: boolean
	preferences: CompanyPreference[]
	bmis: string | null
	recommended_package_id: number | null
	recommended_pricing_model_id: number | null
	company_contact_person: User | null
	country?: POSCountry
	number_of_reward_programs: number
	has_failed_fees?: boolean
	gpo: GroupPurchasingOrganization | null
	enable_non_traditional_hotel_display?: boolean
	feature_flag: CompanyFeatureFlags | null
	domain1?: string
	domain2?: string
}

// eslint-disable-next-line no-restricted-syntax
export enum SubscriptionFeeType {
	CompanySubscription = 'company_subscription',
	User = 'user',
	UserAndCompanySubscription = 'user_company_subscription',
}

// eslint-disable-next-line no-restricted-syntax
export enum PricingModelType {
	Subscription = 'SUBSCRIPTION',
	PayAsYouGo = 'PAY_AS_YOU_GO',
}

// eslint-disable-next-line no-restricted-syntax
export enum FeeType {
	Trip = 'trip',
	SupportRequest = 'support_request',
	TripAndSupportRequest = 'trip_support_request',
}

// eslint-disable-next-line no-restricted-syntax
export enum UserType {
	ActiveUser = 'active_user',
	ProfileUser = 'profile_user',
}

// eslint-disable-next-line no-restricted-syntax
export enum FreePeriodType {
	None = 'none',
	OneTime = 'one_time',
	PerMonth = 'per_month',
	PerYear = 'per_year',
	OneTimePerMonth = 'one_time_per_month',
	OneTimePerYear = 'one_time_per_year',
}

export interface PricingCountryPos {
	annual_payment_discount: number | null
	trip_fee: number | null
	user_fee: number | null
	support_request_fee: number | null
	country_pos_id: number
	company_subscription_fee: number | null
}

export interface PricingModel {
	trip_period_type: FreePeriodType | null
	user_type: UserType | null
	package_id: number
	fee_type: FeeType | null
	subscription_type: SubscriptionFeeType | null
	support_request_value: number | null
	pricing_model_type: PricingModelType
	user_period_type: FreePeriodType | null
	user_value: number | null
	pricing_country_poses: PricingCountryPos[]
	support_request_one_time: number | null
	support_request_period_type: FreePeriodType | null
	trip_value: number | null
	trip_one_time: number | null
	user_one_time: number | null
	id: number
	discount_type: null | 'PERCENTAGE' | 'AMOUNT'
}

export const FEATURE_SCOPE_WEIGHT: Record<FeatureScope, number> = {
	[FeatureScope.Free]: 0,
	[FeatureScope.TravelOnly]: 1,
	[FeatureScope.TravelExpense]: 2,
}

export interface BillingPackage extends WithName {
	id: number
	comments: string | null
	start_date: string
	previous_package_id: number | null
	status: PackageStatus
	original_package_id: number | null
	feature_scope: FeatureScope
	stop_sell_date: string | null
	replaced_by_id: number | null
	pricing_models: PricingModel[]
	migrate_clients: boolean | null
}

interface PosBillingSettingsBase {
	invoice_profile_id: number
	pricing_model_id: number
	country_pos_id: number

	billed_annually: boolean
	active_user_fees_payment_config: ({ invoice_profile_id: number } & WithPaymentMethodId)[] | null
}

export type PosBillingSettings = PosBillingSettingsBase & WithPaymentMethodId

export interface PackagesAndBillingSettings {
	selected_pricing_models_by_pos: PosBillingSettings[]
}

export interface ActiveUserFeePaymentConfig {
	invoice_profile: InvoiceProfile
	credit_card: CreditCardResponse | null
	virtual_payment_automation: VirtualPaymentAutomation | null
}

export interface PaymentInfo {
	id: number

	billed_annually: boolean
	next_payment_billed_annually: boolean

	virtual_payment_automation: VirtualPaymentAutomation | null
	credit_card: CreditCardResponse | null
	country_pos: CountryPOS

	invoice_profile: InvoiceProfile | null

	complimentary_period_message?: string

	package: BillingPackage
	next_payment_package: BillingPackage | null

	pricing_model: PricingModel
	next_pricing_model: PricingModel | null

	start_date: string | null
	next_payment_date: string | null

	active_user_fees_payment_config: ActiveUserFeePaymentConfig[] | null
}

export type CompanyDetails = Pick<Company, 'accepted_tos' | 'bmis'>

export function getRoleLabel({ role, t, count }: { role: Role; t: TFunction; count?: number }) {
	return {
		[Role.Employee]: t('roles.employee', { count }),
		[Role.Manager]: t('roles.manager', { count }),
		[Role.Executive]: t('roles.executive', { count }),
	}[role]
}

export const RolePriority: Record<Role, number> = {
	[Role.Employee]: 0,
	[Role.Manager]: 1,
	[Role.Executive]: 2,
}

export type TravelProfileKeys = keyof TravelProfile

export type TripReportSettings = Record<ReportingFields, Maybe<string>> & WithUserId
export interface WithTripApprovalStatuses {
	approval_statuses: TripApproval[]
}

export interface MinimalTrip {
	id: number
	name?: string | null
	status: TripStatus
}

export interface Trip extends WithTravelers, WithTripApprovalStatuses {
	purpose_list: TripPurposeOption[]
	id: number
	name?: string | null
	status: TripStatus
	calculatedStatus?: AllTripStatuses
	created_dt?: Date | string
	created_by?: number
	created_by_user?: User
	travelers_count: number
	flights_comments: string | null
	hotels_comments: string | null
	cars_comments: string | null
	products: Product[]
	trip_type: FlightDirection
	include_flights: boolean
	include_accommodations: boolean
	support_requests: SupportRequest[]
	pre_trip_request_id?: number
	purpose: TripPurpose[]
	total_cost: number
	total_cost_billed: BillingCost[]
	parent_trip_id?: number
	sub_trips: Trip[] // TODO: add TripBase
	start_date?: string | null
	end_date?: string | null
	fees?: Money
	cancellation_date?: Date | string
	report_settings?: TripReportSettings[]
}

export interface WithReservationStatus {
	reservation_status: ReservationStatus | null
}

export interface Product extends WithCancellationStatus, Partial<WithReservationStatus> {
	product_type: TripProductType
	start_time: string
	end_time: string
	from_location: Location
	to_location: Location
}

/**
 * Another ProductType we are getting from `GET /trip/{id}` endpoint as the `response.product.0.product_type` field.
 * Should have been BookingSegmentProductType, but got `hotel_rooms` by a mistake.
 * Should be refactored on both BE and FE sides to BookingSegmentProductType.
 */
// eslint-disable-next-line no-restricted-syntax
export enum TripProductType {
	Rails = 'rails',
	Flight = 'flights',
	Hotel = 'hotel_rooms',
	Car = 'cars',
}

export function getLocationTypeLabel({ locationType, t }: { locationType: LocationTypes; t: TFunction }) {
	return {
		[LocationTypes.Home]: t('location-type.home'),
		[LocationTypes.Office]: t('location-type.office'),
		[LocationTypes.Client]: t('location-type.client'),
		[LocationTypes.Hotel]: t('location-type.hotel'),
		[LocationTypes.Airport]: t('location-type.airport'),
		[LocationTypes.Other]: t('location-type.other'),
		[LocationTypes.City]: t('location-type.city'),
		[LocationTypes.Country]: t('location-type.country'),
		[LocationTypes.CarRentAgency]: t('location-type.car-rent-agency'),
		[LocationTypes.TrainStation]: t('location-type.train'),
		[LocationTypes.Rail]: t('location-type.rail'),
		[LocationTypes.AllStation]: t('location-type.all-station'),
	}[locationType]
}

export interface RailLocations extends Location {
	stations: Array<{
		external_id: ExternalLocationId
	}>
}

export type SearchLocation = Location | RailLocations

export interface Country {
	name: z.infer<typeof CountryNameZ>
	iso: string
	calling_code: string
}

// eslint-disable-next-line no-restricted-syntax
enum LuggageStatus {
	None = 'NONE',
	Free = 'FREE',
	Paid = 'PAID',
}

// eslint-disable-next-line no-restricted-syntax
export enum InvoiceProfileStatus {
	ConfirmationInProgress = 'confirmation_in_progress',
	TermsOfServiceValidationPending = 'validation_pending',
	Active = 'active',
}

// eslint-disable-next-line no-restricted-syntax
export enum TripStatusVirtual {
	Upcoming = 'upcoming',
	TravelingNow = 'traveling_now',
	PastTrip = 'past_trip',
	Rejected = 'rejected',
}

export function getTripStatusLabel({
	status,
	count,
	t,
}: {
	status: TripStatus | TripStatusVirtual
	count?: number
	t: TFunction
}) {
	if (status === TripStatus.WaitingApproval) {
		return t('trips-page.status-list-titles.waiting-approval', { count })
	}

	if (status === TripStatus.Draft) {
		return t('trips-page.status-list-titles.draft', { count })
	}
	if (status === TripStatus.Booked) {
		return t('trips-page.status-list-titles.booked', { count })
	}
	if (status === TripStatus.NotApproved) {
		return t('trips-page.status-list-titles.not-approved', { count })
	}
	if (status === TripStatus.Canceled) {
		return t('trips-page.status-list-titles.canceled', { count })
	}
	if (status === TripStatusVirtual.PastTrip) {
		return t('trips-page.status-list-titles.past-trip', { count })
	}

	return null
}

export const TripStatusLabelMap: Partial<Record<TripStatus | TripStatusVirtual | InvoiceProfileStatus, string>> = {
	[TripStatus.Booked]: 'trip-statuses.booked',
	[TripStatus.Canceled]: 'trip-statuses.canceled',
	[TripStatus.Draft]: 'trip-statuses.draft',
	[TripStatus.DraftBySupport]: 'trip-statuses.draft-by-support',
	[TripStatus.WaitingApproval]: 'trip-statuses.pending-approval',
	[TripStatus.NotApproved]: 'trip-statuses.rejected',
	[TripStatusVirtual.TravelingNow]: 'trip-statuses.traveling-now',
	[TripStatusVirtual.Upcoming]: 'trip-statuses.upcoming',
	[TripStatusVirtual.PastTrip]: 'trip-statuses.past',
	[TripStatus.Reverted]: 'trip-statuses.booking-failed',
	[TripStatus.Booking]: 'trip-statuses.booking',
	[TripStatus.Approved]: 'trip-statuses.approved',
	[InvoiceProfileStatus.ConfirmationInProgress]: 'invoice-profile-statuses.confirmation-in-progress',
	[InvoiceProfileStatus.TermsOfServiceValidationPending]:
		'invoice-profile-statuses.terms-of-service-validation-pending',
}

export const getTripStatusLabels = (
	t: TFunction,
	approverName?: string,
): Record<TripStatus & TripStatusVirtual & InvoiceProfileStatus, string> => {
	return {
		[TripStatus.Booked]: t('trip-statuses.booked'),
		[TripStatus.Canceled]: t('trip-statuses.canceled'),
		[TripStatus.Draft]: t('trip-statuses.draft'),
		[TripStatus.DraftBySupport]: t('trip-statuses.draft-by-support'),
		[TripStatus.WaitingApproval]: t('trip-statuses.pending-approval'),
		[TripStatus.NotApproved]: t('trip-statuses.rejected'),
		[TripStatusVirtual.TravelingNow]: t('trip-statuses.traveling-now'),
		[TripStatusVirtual.Upcoming]: t('trip-statuses.upcoming'),
		[TripStatusVirtual.PastTrip]: t('trip-statuses.past'),
		[TripStatus.Reverted]: t('trip-statuses.booking-failed'),
		[TripStatus.Booking]: t('trip-statuses.booking'),
		[TripStatus.PriceChanged]: t('trip-statuses.booking'),
		[TripStatus.Approved]: t('trip-statuses.approved'),
		[InvoiceProfileStatus.ConfirmationInProgress]: t('invoice-profile-statuses.confirmation-in-progress'),
		[InvoiceProfileStatus.TermsOfServiceValidationPending]: t(
			'invoice-profile-statuses.terms-of-service-validation-pending',
		),
		[TripStatus.WaitingForUserSubmission]: t('trip-approvers-list.approved-by', { name: approverName }),
		[TripStatus.UrgentActionRequired]: t('trip-statuses.urgent-action-required'),
	}
}

export function getEmptyStatesForNoTripsOfTypeTitle({
	status,
	t,
}: {
	status: TripStatus | TripStatusVirtual
	t: TFunction
}) {
	const commonTitleStatuses = [
		TripStatus.WaitingApproval,
		TripStatus.NotApproved,
		TripStatus.Canceled,
		TripStatusVirtual.PastTrip,
	]
	if (commonTitleStatuses.includes(status)) {
		const statusLabel = TripStatusLabelMap[status] as string
		return statusLabel
			? t('trips-page.empty-states.no-trips-of-type.title', { tripType: t(statusLabel).toLowerCase() })
			: null
	}

	// this titles specifically used for French language as received requirement from business
	if (status === TripStatus.Draft) {
		return t('trips-page.empty-states.no-trips-of-type.title-draft')
	}
	if (status === TripStatus.Booked) {
		return t('trips-page.empty-states.no-trips-of-type.title-booked')
	}

	return null
}

export type AllTripStatuses =
	| TripStatus
	| TripStatusVirtual
	| ApprovalStatus
	| InvoiceProfileStatus
	| CompanyInvoiceStatus

export type SortTopFlightResults = Record<FlightFilterSet['sortBy'], FlightResult | undefined>
export type SortTopRailResults = Record<FlightFilterSet['sortBy'], RailResult | undefined>
export type SortTopHotelResults = Record<HotelFilterSet['sortBy'], HotelResult>
export type SortTopCarResults = Record<CarFilterSet['sortBy'], CarResult>

export interface TripFilterSet {
	departingFrom: Date | null
	departingTo: Date | null
	destinationName: string | null
	traveler: User | null
	creator: User | null
	office: Office | null
}

export type StopsFilterNumbers = 0 | 1 | 2

export type StopsFilter = Record<StopsFilterNumbers, boolean>

export interface RefundableFilter {
	hideNonRefundable: boolean
	hideNonChangeable: boolean
	hideUnknownChangeable: boolean
	hideUnknownRefundable: boolean
}

export interface FlightTimeRange {
	depart: Date[]
	arrive: Date[]
}

export type ConnectionNumberFilter = 0 | 1 | 2 | undefined

export type TransportOnly = ProductType.Flights | ProductType.Rails
export type TransportFilterState = Pick<Record<ProductType, boolean>, TransportOnly>

export interface FlightFilterSet {
	connectionNumber: ConnectionNumberFilter
	stops: StopsFilter
	onlyInPolicy: boolean
	onlyRefundable: RefundableFilter
	cabinClass: Record<string, boolean>
	departureAirports: Record<string, boolean>
	arrivalAirports: Record<string, boolean>
	airlines: Record<string, boolean>
	airlinesMixed: boolean
	hideLcc: boolean
	sortBy: 'lowestCO2' | 'duration' | 'earliestArrival' | 'earliestDeparture' | 'price' | 'recommended'
	price: [number, number] | null
	duration: Array<number[] | null>
	flightUniqueKey: string[]
	times: FlightTimeRange[]
	connectionTime: number[] | undefined
	connectionAirports: Record<string, boolean | undefined>
	providers: Record<string, boolean>
	transportType: TransportFilterState
}

export interface HotelTimeRange {
	checkIn: Date[]
	checkOut: Date[]
}

export type HotelSortOptions = 'recommended' | 'price' | 'distance'

export interface HotelFilterSet extends WithName {
	onlyInPolicy: boolean
	sortBy: HotelSortOptions
	stars: Record<string, boolean>
	price: [number, number] | null
	times: HotelTimeRange
	neighborhoods: Record<string, boolean>
	distance: number[] | null
	hotelId: string
	mapBoundingBox: LongitudeLatitudeBounds | null
	chains: Record<string, boolean>
	hideSoldOut: boolean
}

export interface CarPaymentFilter {
	directBilling: boolean
	paymentAtDropOff: boolean
}

export interface CarFilterSet {
	onlyInPolicy: boolean
	sortBy: 'recommended' | 'price'
	price: [number, number] | null
	rentalAgency: Record<string, boolean>
	carGroup: Record<string, boolean>
	gear: Record<CarTransmission, boolean>
	mileageUnlimited: boolean
	fuel: Record<CarFuel, boolean>
	vendors: Record<string, boolean>
	payment: CarPaymentFilter
}

// eslint-disable-next-line no-restricted-syntax
export enum HotelRoomPaymentOptionFilterValue {
	All = 'all',
	PayAtProperty = 'payAtProperty',
	OnlinePrepayment = 'onlinePrepayment',
}

export interface HotelRoomsFilterSet {
	refundable: boolean
	inPolicy: boolean
	breakfastIncluded: boolean
	paymentOption: HotelRoomPaymentOptionFilterValue
	loyalty: boolean
	vpaAccepted: boolean
}

export interface HotelSourceFilterSet {
	BookingCom: boolean
	Expedia: boolean
	Sabre: boolean
	PremierInn: boolean
}

// eslint-disable-next-line no-restricted-syntax
export enum HotelSource {
	BookingCom = 'bookingcom',
	Expedia = 'eps',
	Sabre = 'sabre',
	PremierInn = 'premierinn',
}

export type ClassName = {
	className?: string
}

export type DefaultProps = React.PropsWithChildren<{
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	id?: any
	style?: React.CSSProperties
	'data-test'?: string
}> &
	ClassName

export type PageProps = DefaultProps

export type FlightDirection = TripDirection.OneWay | TripDirection.RoundTrip | TripDirection.MultiDestination

export type RailDirection = TripDirection.OneWay | TripDirection.RoundTrip

export interface GeoPoint {
	longitude: number
	latitude: number
}

export interface MapPoint extends GeoPoint, WithName {
	address: string
	location: Location
}

export interface JointMapPoint extends MapPoint {
	points: MapPoint[]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface SelectOption<T = any, L = React.ReactNode> {
	checked?: boolean
	label: L
	value: T
	description?: React.ReactNode
	disabled?: boolean
}

type RenderFunction<T> = (this: T) => React.ReactNode

export type BindableLabel<T> = React.ReactNode | RenderFunction<T>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SelectOptionsBuilder<C, T = any> = SelectOption<T, BindableLabel<C>>

export interface SearchParams {
	limit: number
	last_seen_result_index?: number
}

export interface SearchResult {
	status: SearchJobStatus
	trip_id: TripId
	id: SearchId
	flight_results: FlightResults | null
	hotel_results: HotelResults | null
	car_results: CarResults | null
	rails_results: RailResults | null
	price_metadata: PricesMetadata
	co2_metadata?: StatsBucketsPerItem
}

export interface PriceGaugeSet {
	current: StatsBuckets | null
	all: PricesMetadata | null
	label?: React.ReactNode
}

export interface SearchResults<T = unknown> {
	results: T[]
	last_seen_result_index: number
	max_result_index: number
}

export interface PaginatedSearchResults<T = unknown> extends SearchResults<T> {
	previous_results_ids: number[]
	finished: boolean
	finished_pages: number
	results_finalized?: boolean
	total_pages: number
}

interface FlightResults extends SearchResults<FlightResult> {
	airlines: Airline[]
}

export type RailTransportMode =
	| 'bus'
	| 'ferry'
	| 'hovercraft'
	| 'metro'
	| 'Local train'
	| 'taxi'
	| 'train'
	| 'tram'
	| 'tramlink'
	| 'transfer'
	| 'walk'

export type RailLeg = {
	id: RailLegId
	arrive_at: string
	carrier: {
		/** @example London North Eastern Railway */
		description: string
		/** @example urn:trainline:atoc:carrier:GR */
		code: string
		/** @example London North Eastern Railway */
		name: string
	}
	depart_at: string
	destination: {
		country_code: string
		location_type: string
		name: string
		time_zone: string
	}
	distance: number
	duration: string
	is_replacement_service: boolean
	origin: {
		country_code: string
		location_type: string
		name: string
		time_zone: string
	}
	platforms?: {
		destination: {
			platform?: string | null
		}
		origin: {
			platform?: string | null
		}
	}
	co2_emissions: number | null
	route: string | null
	service_information: null
	timetable_id: string
	transport: {
		description: string
		designation: string | null
		mode: RailTransportMode
	}
}

export type RailResultFareType = {
	description: string
	conditions: Array<{
		name: string
		description: string
	}>
	conditions_summary: string
	validity_period_inward: string | null
	validity_period_outward: string
	secondary_fare: boolean
	fare_pricing_category: string
}

export type RailLocation = {
	country_code: string
	location_type: string
	name: string
	time_zone: string
}

export type RailItineraryFare = {
	description: string
	conditions: Array<{ name: string; description: string }>
	conditions_summary: string
	route_restriction: string | null
	permitted_destinations: RailLocation[]
	permitted_origins: RailLocation[]
	type: RailResultFareType
	passengers: Array<{
		id: string
		type: {
			age_restriction: {
				lower_bound: number
				upper_bound: number | null
			}
			name: string
		}
	}>
}

export type RailFare = {
	title?: string
	description: string
	conditions: Array<{ name: string; description: string }>
	conditions_summary?: string
	route_restriction?: string | null
	permitted_destinations: RailLocation[] | undefined
	permitted_origins: RailLocation[] | undefined
	legs?: AlternativeFareLeg[]
}

export type AppliedRailCard = { card_id: string; passenger: string }

export type AppliedDiscount = {
	name: string
	description: string
	industry_code: string | null
	conditions: string[]
}

export type AlternativeFareLeg = {
	id: string
	travel_class: string
	comfort_class: {
		name: string
		description: string
	} | null
}

export type AlternativeFareType = {
	price: {
		amount: string
		currency: Currency
		currency_code: Currency
	}
	full_price: {
		amount: string
		currency: string
		currency_code: string
	}
	type: RailResultFareType
	category: {
		description: string
		classification: string
	}
	legs?: AlternativeFareLeg[]
	route_restriction: string | null
	valid_from: {
		outward: string
		inward: string | null
	}
	valid_until: {
		outward: string
		inward: string | null
	}
	origin: {
		country_code: string
		location_type: string
		name: string
		time_zone: string
	}
	destination: {
		country_code: string
		location_type: string
		name: string
		time_zone: string
	}
	local_area_validity: string | null
	discount_card_ids: string[]
	passengers: Array<{
		id: string
		type: {
			age_restriction: {
				lower_bound: number
				upper_bound: number | null
			}
			name: string
		}
	}>
	applied_discount: string | null
	permitted_origins: Array<RailLocation>
	permitted_destinations: Array<RailLocation>
	availability: {
		status: string
		remaining: number | null
	}
	converted_price?: {
		amount: string
		currency: UserCurrency
	}
}

export type RailResultAlternative = {
	id: string
	price: {
		amount: string
		currency: string
		currency_code: string
	}
	full_price: {
		amount: string
		currency: string
		currency_code: string
	} | null
	fares: AlternativeFareType[]
	outward_fares: AlternativeFareType[]
	inward_fares: AlternativeFareType[]
	applied_discount: AppliedDiscount | null
	flexibility: string | null
	travelling_groups: unknown[]
	included_extras: unknown[]
	vendor: {
		description: string
	}
	inventory: {
		code: 'atoc' | 'sncf' | 'db'
		name: string
	}
	converted_price: {
		amount: string
		currency: UserCurrency
	}
	converted_full_price: {
		amount: string
		currency: UserCurrency
	} | null
	rail_cards?: AppliedRailCard[]
} & WithPolicy

export type RailResult = {
	id: string
	arrive_at: string
	depart_at: string
	direction: string
	duration: string
	legs: RailLeg[]
	provider_name: RailProviderName
	sections: {
		alternatives: RailResultAlternative[]
	}[]
	unsellable_reason?: {
		long_description: string
		short_description: string
	}
	alternatives_required_for_inward_search: boolean
	recommendation_score: number
}

export type RailResults = {
	search_id: RailSearchId
	inward_search_id?: string
	type: string
	passengers: {
		cards: null
		date_of_birth: string
		id: string
		type: {
			age_restriction: {
				lower_bound: number
				upper_bound: number | null
			}
			name: string
		}
	}[]
	expires_at: null
	pagination_is_available: boolean
	outward_journeys?: RailResult[]
	inward_journeys?: RailResult[]
	carriers: {
		description: string
		code: string
		name: string
	}[]
	page_direction?: 'next' | 'previous'
	previous_is_available: boolean
	next_is_available: boolean
}

export interface Airport extends WithName {
	city_code: null | string
	country_iso: string
	country_name: string
	location_type: LocationTypes
	location_data: { city_code?: string; iata?: string }
	iata: string
	id: number
	lat: number
	long: number
	size: string
	code: string
}

export interface CancellationPolicies {
	cancellation_policy: string | null
	change_penalty_after: number | null
	change_penalty_before: number | null
	cancellation_penalty_after: number | null
	cancellation_penalty_before: number | null
	cancellation_due_date: string | null
	ticket_refundable: boolean
	penalties: FlightPenaltiesArray | null
}

export interface ServiceDiscount {
	is_exclusive: boolean
	is_company_discount: boolean
	label: string | null
	code: string | null
	discount_code?: string | null
}

export interface FlightDiscount extends ServiceDiscount {
	is_bcd_nego_fare: boolean
}

export interface HotelDiscount extends ServiceDiscount {
	is_member_only: boolean
	program_name: string | null
	registration_url: string | null
}

// eslint-disable-next-line no-restricted-syntax
export enum BookingType {
	ExternalBooking = 'external_booking',
}

export interface WithDiscounts<T> {
	discounts: T[]
}

export type WithFlightDiscounts = WithDiscounts<FlightDiscount>

export type WithHotelDiscounts = WithDiscounts<HotelDiscount>

export interface WithTravelers {
	travelers: User[]
}

export interface WithCvvRequirement {
	cvv_required: boolean | null
}

export interface WithBookingType {
	booking_type?: BookingType
	created_by_user?: { id: UserId } & PersonNames
	created_dt?: string
}

export interface WithBookingSegmentToTravelers {
	booking_segment_to_travelers: BookingSegmentToTraveler[]
}

export interface WithRailExtraBookingData {
	extra_booking_data: {
		delivery_options: DeliveryOptions[]
		cancellation_description?: string
		cancellation_due_date?: string
		ticket_delivery_fee?: string
		price_per_person?: PastBillingCost
	}
}

export interface WithOriginalBookingSegmentId {
	original_booking_segment_id: number | null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface FlightResult<T extends FlightResultMinimal<any> = Flight>
	extends
		Omit<IndexedSearchResult, 'id'>,
		Omit<BookingSegment, 'id'>,
		WithTravelers,
		WithCvvRequirement,
		WithBookingType,
		WithBookingSegmentToTravelers {
	fare_group_key: FlightFareGroupKey
	flight_option_keys?: {
		traveler_id: number
		flight_option_keys: FlightOptionKey[]
	}[]
	flights: T[]
	provider: Uppercase<ValueOf<typeof FlightProvider>>
	/** provider + fare_components ( airline_code + fare_basis_code ) + res_id */
	flight_identifier: FlightIdentifier
	/**
	 * @deprecated
	 * Formula: concat ( total_price, concat ( flights.0.key ) )
	 */
	res_id: string
	total_duration: number
	total_tax: number
	vendor: Uppercase<ValueOf<typeof FlightProvider>>
	hold_luggage_price: number | null
	fare_components: FlightFareComponent[]
	missing_ffn_information: MissingFrequentFlyerInformation
	is_active: boolean
	ticket_refundable: boolean
	ticket_by_date: Date | string
	uta: BookingOptionUTA
	validating_carrier_codes: string[] | null

	external_booking_feedback?: string
	payment_cards_accepted: CreditCardType[]
	support_request_fees?: SupportRequestFeeForm[]
}

export interface WithPolicy {
	in_policy: boolean | null
	policy_deviations: FlightPolicyDeviation[]
}

export type FlightResultMinimal<T = MinimalSegment & WithFlightDiscounts> = {
	/** The number of available seats if known. Available only on a booking segment. */
	remaining_seats_amount: number | null
	segments: T[]
	/**
	 * Provides a stable key of the slice not connected to the fare,
	 * would be the same between different fare groups.
	 * Formula: concat ( segment.0.key )
	 */
	id: FlightSearchSliceId
}

export type Flight = FlightResultMinimal<Segment> & {
	flight_option_key: FlightOptionKey
	duration: {
		hours: number
		minutes: number
	}
	seats: number
	/** Database ID of the flight slice in the booking segment */
	flight_id: FlightSliceId
	locations: Location[]
	depart_at: string
	land_at: string
	flight_class: string
	flight_airline: Airline
	flight_number: string
	price: number
	luggage_status?: LuggageStatus
	recommendation_score: number
}

interface SegmentToUser extends WithUserId {
	seat_type: string | null
	seat: string | null
	airline_confirmation: string | null
	eticket: string | null
}
export interface AirportASB {
	code: string
	location: string | null
	name: string | null
	timezone: string
	title: string
}

export interface MinimalSegment {
	airplane: string
	aircraft_type: string
	airport_change: boolean
	arrival: TimeAndLocation
	amenities: FlightAmenity[]
	baggage_allowance: FlightBaggageAllowance | null
	departure: TimeAndLocation
	cabin: CabinClassName
	/** It's fare_component.price_class_name */
	cabin_commercial_name?: string | null
	carrier: FlightCarrier
	operating_carrier: FlightCarrier
	//OLD values
	marketing_airline: Airline
	departure_airport: Airport
	arrival_airport: Airport
	cabin_class: CabinClassName
	departure_time: string
	arrival_time: string
	number_of_travelers: number
	flight_number: string
	bags_recheck_required: boolean
	segment_to_users: SegmentToUser[]
	seat_selection: (SeatMapSeat & WithPolicy)[]
	/** Database ID of the flight segment in the booking segment */
	id: FlightSegmentId
	fare_rules?: FareComponent
	emissions: number | null
}

export interface SeatMapSeatDeletion {
	seat_keys: string[]
}

interface StopAirports {
	code: number
	departure_time: string
	arrival_time: string
}

export interface TimeAndLocation {
	airport: AirportASB
	datetime: string
	terminal: string
}

export interface Segment {
	aircraft_type: string
	airplane: string
	airport_change: boolean
	arrival: TimeAndLocation
	amenities: FlightAmenity[] | null
	departure: TimeAndLocation
	cabin: CabinClassName
	duration: {
		hours: number
		minutes: number
	}
	carrier: FlightCarrier
	operating_carrier: FlightCarrier
	/**
	 * Provides a stable key of the segment not connected to the fare (and its amenities),
	 * would be the same between different fare groups.
	 * Formula: hash ( carrier, departure, arrival )
	 * */
	id: FlightSearchSegmentId
	/** It's fare_component.price_class_name */
	cabin_commercial_name: string
	emissions: number | null

	//old
	arrival_airport_id: number
	arrival_terminal: null | string
	arrival_time: string
	baggage_allowance: FlightBaggageAllowance | null
	cabin_class: CabinClassName
	departure_airport_id: number
	departure_terminal: null | string
	departure_time: string
	distance_in_miles: number
	flight_duration: number
	flight_number: string
	marketing_airline_id: number
	marketing_airline?: Airline
	operating_airline_id: number
	operating_airline?: Airline
	plane_type: string
	number_of_bags: number
	baggage_weight: string
	baggage_description: string
	vendor_specific_info: VendorSpecificInfo
	bags_recheck_required: boolean
	stop_airports: StopAirports
	segment_id_ref: FlightSegmentRefId
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type VendorSpecificInfo = any

export type HotelResults = PaginatedSearchResults<HotelResult> & {
	bulk_id: string
	page_results: null
	hotels_found_total: number
}

export type CarResults = PaginatedSearchResults<CarResult>

export const CabinClassLabels = {
	premium_first: 'flights.cabin-class-name.premium-first',
	first: 'flights.cabin-class-name.first',
	premium_business: 'flights.cabin-class-name.premium-business',
	business: 'flights.cabin-class-name.business',
	premium_economy: 'flights.cabin-class-name.premium-economy',
	economy: 'flights.cabin-class-name.economy',
	first_train: 'flights.cabin-class-name.first-class-train',
	second_train: 'flights.cabin-class-name.second-class-train',
}

export function getCabinClassLabel({ cabinClassName, t }: { cabinClassName: CabinClassName; t: TFunction }) {
	return {
		premium_first: t('flights.cabin-class-name.first-premium'),
		first: t('flights.cabin-class-name.first'),
		premium_business: t('flights.cabin-class-name.business-premium'),
		business: t('flights.cabin-class-name.business'),
		premium_economy: t('flights.cabin-class-name.premium-economy'),
		economy: t('flights.cabin-class-name.economy'),
		first_train: t('flights.cabin-class-name.first-class-train'),
		second_train: t('flights.cabin-class-name.second-class-train'),
	}[cabinClassName]
}

export function getRailTravelClassLabel({ travel_class, t }: { travel_class: RailTravelClass; t: TFunction }) {
	return (
		{
			first: t('flights.cabin-class-name.first-class-train'),
			second: t('flights.cabin-class-name.second-class-train'),
		}[travel_class] ?? t('flights.cabin-class-name.second-class-train')
	)
}

export const CabinClassCodeLabels = {
	P: CabinClassLabels.premium_first,
	F: CabinClassLabels.first,
	J: CabinClassLabels.premium_business,
	C: CabinClassLabels.business,
	S: CabinClassLabels.premium_economy,
	Y: CabinClassLabels.economy,
}

export type CabinClassName = keyof typeof CabinClassLabels & string
export const CabinClassNames: Record<CabinClassName, CabinClassName> = Object.keys(CabinClassLabels).reduce(
	(a, k) => ({ ...a, [k]: k }),
	{} as Record<CabinClassName, CabinClassName>,
)

export const FLIGHT_CABIN_CLASS_NAMES = [
	CabinClassNames.economy,
	CabinClassNames.premium_economy,
	CabinClassNames.business,
	CabinClassNames.first,
]

export const TRAIN_CABIN_CLASS_NAMES = [CabinClassNames.second_train, CabinClassNames.first_train]

export function getFlightDirectionLabel({ direction, t }: { direction: FlightDirection; t: TFunction }) {
	return {
		[TripDirection.OneWay]: t('flights.flight-directions.one-way'),
		[TripDirection.RoundTrip]: t('flights.flight-directions.round-trip'),
		[TripDirection.MultiDestination]: t('flights.flight-directions.multi-destination'),
	}[direction]
}
export function getRailDirectionLabel({ direction, t }: { direction: RailDirection; t: TFunction }) {
	return {
		[TripDirection.OneWay]: t('One way'),
		[TripDirection.RoundTrip]: t('Round Trip'),
	}[direction]
}

export interface WithSearchId {
	search_id: number
}

interface IndexedSearchResult extends WithSearchId {
	id: BookingSegmentId
	result_index: number
	res_id: string | null
	search: { price_metadata: PricesMetadata; co2_metadata?: StatsBucketsPerItem }
	currency?: UserCurrency
	local_currency?: UserCurrency
	vendor_specific_info?: VendorSpecificInfo
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	raw_data?: any
	readonly __original_total_price?: BillingCost | null
	readonly __original_total_tax?: BillingCost | null
}

// eslint-disable-next-line no-restricted-syntax
export enum AccommodationType {
	All = 'all',
	Apartment = 'apartment',
	BnB = 'bnb',
	ConventionCenter = 'convention_center',
	Economy = 'economy',
	Experience = 'experience',
	Hotel = 'hotel',
	Leisure = 'leisure',
	Other = 'other',
}

export interface HotelResult extends WithPolicy, Omit<IndexedSearchResult, 'id'>, WithName {
	id: string
	is_available: boolean
	min_avg_rate: number
	min_avg_rate_local_currency: number
	min_total: number
	min_total_local_currency: number
	min_rate_breakfast_included: boolean | null
	promo_messages: string[]
	tmc_preferred: boolean
	tmc_preference_tier: number | null
	/** @deprecated */
	client_preferred: boolean
	client_preference_tier: number | null
	warnings: Warning[]
	address: string
	property_amenities: HotelAmenityValues[]
	room_amenities: HotelAmenityValues[]
	chain_info: ChainInfo[]
	check_in_time: string | null
	check_out_time: string | null
	city: string
	country_code: string
	airport_code: string
	direction: string
	distance: number
	hotel_rating: number | null
	iata_guarantee: boolean
	latitude: number
	longitude: number
	phone: string | null
	postal_code: string
	regions: Regions
	sort_metric: SortMetric[]
	state_province_code: null
	thumbnails: string[]
	trustyou_rating: number | null
	is_bcd_great_rate_hotel?: boolean
	is_excluded?: boolean
	liked_users: User[]
	recommendation_score: number
	accommodation_type: AccommodationType
}

type CarVendorSpecificInfo = {
	[key in Lowercase<CarVendor>]: VendorInfo
}

export interface WithCarVendorInfo {
	vendor_specific_info?: CarVendorSpecificInfo
	vendor: CarVendor
}

export interface CarResult extends WithPolicy, Omit<IndexedSearchResult, 'vendor_specific_info'>, WithCarVendorInfo {
	total_price: number
	recommendation_score?: number
	car_type: CarType
	pickup_location: CarAgencyLocation
	return_location: CarAgencyLocation | null
	pickup_datetime: moment.MomentInput
	return_datetime: moment.MomentInput
	rent_agency: CarRentAgency
	num_of_seats: number
	large_bags: number | null
	small_bags: number | null
	mileage: Mileage
	thumbnail: string
	cancellation_details?: CarCancellationPolicy | null
	commission: number | string | null
	additional_details: Record<CarAdditionalDetailsName, CarRateDescription[] | null> & {
		special_equip: CarSpecialEquipment[]
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		models: any
		address: string | null
		agency_name: string | null
	}
	prepaid: boolean
	prepaid_price?: number
	direct_billing_agreement?: boolean
	direct_billing_agreement_not_applicable?: boolean
}

export interface HotelDescriptionFields {
	description: string
	short_description: string
	dining: string
	area_attraction: string
	policy: string
	recreation: string
	what_to_expect: string
}

export interface HotelInfo {
	hotel: HotelData
	liked_users: User[]
}

export interface HotelData extends HotelDescriptionFields, WithName {
	city: string
	vendor_hotel_id: string
	room_amenities: HotelAmenityValues[]
	address: string
	property_amenities: HotelAmenityValues[]
	country_code: string
	liked_users: User[]
	star_rating: number | null
	rating: number | null
	thumbnails: string[]
	long: number
	lat: number
	checkin_time: string
	checkout_time: string
	property_type: string
	has_ecofriendly_badge: boolean
}

export interface RoomRate extends HotelBooking {
	rate_key: string
	rate_group_name?: string
	loyalty_card_allowed?: boolean
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	loyalty_programs?: any[]
	total_price_billed: BillingCost
}

export interface WithClonedFromId {
	cloned_from_id: number | null
}

export interface WithVpaAccepted {
	vpa_accepted: boolean
}

export interface WithPayAtCheckout {
	pay_at_checkout: boolean
}

export interface HotelBooking
	extends
		WithPolicy,
		IndexedSearchResult,
		BookingSegment,
		WithTravelers,
		WithHotelDiscounts,
		WithClonedFromId,
		WithCvvRequirement,
		WithVpaAccepted,
		WithPayAtCheckout {
	room_description: string
	/** total price * `n` nights. divide by night count for avg. price per night */
	total_price: number
	room_amenities: HotelAmenityValues[]
	non_refundable: boolean
	original_total_tax: number
	total_tax: number
	original_total_price: number
	cancel_by: string | null
	raw_data: HotelBookingRawData
	average_per_night: number
	original_currency: string
	/** @deprecated */
	client_preferred: boolean
	check_in: string
	check_out: string
	cancellation_due_date: string | null
	payment_cards_accepted: CreditCardType[]

	special_request_allowed: boolean
	special_request_length: number | null
	special_request_type: SpecialRequestType
}

interface HotelBookingRawData extends WithPolicy {
	additional_rate_information: null
	average: number
	cancel_by: string
	cancellation_policy: string
	cancellation_policy_known: boolean
	change_during_stay: boolean
	/** @deprecated */
	client_preferred: boolean
	commission: Commission
	currency: string
	is_bcd_great_rate: boolean
	is_mobile: boolean
	is_package_rate: boolean
	local_average: number
	local_taxes: number
	local_total: number
	members_only: boolean
	non_refundable: boolean
	original_average: number
	original_currency: string
	original_taxes: number
	original_total: number
	paid_policy: string
	payment_options: string[]
	provider: string
	rate_code: string
	rate_company_name: null
	room_description: string
	rate_key: string
	rate_promoline: null
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	rate_value_adds: any[]
	room_type_code: string
	tax_mark: string
	taxes: number
	tmc_preferred: boolean
	total: number
	billing_address_required: boolean
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	loyalty_programs: any[]
	payment_cards_accepted: PaymentCardsAccepted[]
	special_requests: SpecialRequests
}

export interface LoyaltyProgram {
	loyalty_card_chain_code: string
	loyalty_card_chain_name: string
}

export interface ServiceLoyaltyCardFormData {
	loyalty_program_id?: number | string
	loyalty_program_code: string
}

interface PaymentCardsAccepted {
	code: string
	mandatory_checkout_text: null
}

// eslint-disable-next-line no-restricted-syntax
export enum SpecialRequestType {
	Text = 'text',
	Keys = 'keys',
	KeysAndValues = 'keys_values',
}

interface SpecialRequests {
	sr_allowed: boolean
	sr_length: number
}

interface Warning {
	type: string
	provider?: string
	message: string
}

interface ChainInfo {
	chain_code: string
	chain_name: string
	master_chain_code: string | null
	master_chain_name: string | null
}

interface Regions {
	neighborhood: string[]
}

interface SortMetric extends WithName {
	ascending: boolean
	value: number | null
}

interface Commission {
	commissionable: boolean
}

export interface ItineraryTravelers {
	user: User
	passport_valid: boolean
	documents: TravelerDocuments
	is_passport_expired: boolean
	is_national_ID_expired: boolean
	personal_document_needed: boolean
	document_selection_available: boolean
	has_missing_documents: boolean
	has_missing_date_of_birth: boolean
	has_missing_invoice_profile: boolean
	has_missing_or_incorrect_report_settings: boolean
	has_missing_company_details: boolean
	has_missing_gender?: boolean
}

export interface BookingSegment
	extends WithPolicy, CancellationPolicies, WithCancellationStatus, WithReservationStatus, WithTravelers, WithSearchId {
	id: BookingSegmentId
	total_price: number
	res_id: string | null
}

export type ItineraryFlightBooking = Omit<
	FlightResult<FlightResultMinimal>,
	'fare_components' | 'fare_group_key' | 'flight_option_keys' | 'res_id'
> &
	WithClonedFromId &
	WithOriginalBookingSegmentId &
	WithBookingType & {
		id: BookingSegmentId
		/**
		 * @deprecated
		 * Used for analytics
		 * Formula: concat ( total_price, concat ( flights.0.key ) )
		 */
		res_id: FlightBookingSegmentResId
		recommendation_score: number
	}

export type LateCheckInInterval = [string, string | null]

export interface WithSpecialRequestData {
	arriving_after: LateCheckInInterval | null
	additional_request: string | null
}

export interface ItineraryHotelBooking
	extends
		HotelBooking,
		WithBookingSegmentToTravelers,
		WithSpecialRequestData,
		WithOriginalBookingSegmentId,
		WithBookingType {
	hotel_id: string
	hotel_name: string
	city: string
	external_booking_feedback?: string
	vendor?: IHotelContentSource
	other_vendor?: string
	support_request_fees?: SupportRequestFeeForm[]
}

export interface VendorInfo {
	country_code: string
	exclusive: boolean
	company_discount: boolean
	rate_code?: string
	direct_billing_agreement: boolean
	corporate_discount_code?: string
	direct_billing_agreement_not_applicable?: boolean
}

export interface ItineraryCarBooking
	extends
		BookingSegment,
		WithCarVendorInfo,
		WithClonedFromId,
		WithCvvRequirement,
		WithBookingSegmentToTravelers,
		WithBookingType {
	car: CarBooking
	additional_equip: CarSpecialEquipment[]
	extra_booking_data: CarRate | null
	drop_car_off_at_different_location?: boolean
	__original_total_tax?: OriginalAmount
	car_description?: string
	gear_type?: string
	fuel_type?: string
	pnr?: string
	__original_total_price?: OriginalAmount
	pay_at_checkout?: boolean
	car_confirmation_number?: string
	supplier_confirmation_number?: string
	external_booking_feedback?: string
	support_request_fees?: SupportRequestFeeForm[]
}

export interface CarBooking
	extends
		Pick<
			CarResult,
			| 'res_id'
			| 'id'
			| 'large_bags'
			| 'small_bags'
			| 'car_type'
			| 'pickup_location'
			| 'return_location'
			| 'cancellation_details'
			| 'search'
			| 'vendor'
			| 'prepaid'
			| 'additional_details'
			| 'mileage'
			| 'num_of_seats'
		>,
		WithBookingType {
	fees: CarFee[]
	car_rent_vendor: CarRentAgency
	start_time: string
	end_time: string
	thumbnail: string
}

export interface RailDestination {
	id: string
	city: string | null
	country: string
	latitude: number
	longitude: number
	name_en: string | null
	name_local: string | null
}
interface RailItineraryDestination {
	datetime: string
	station: RailDestination
}

export type RailSegmentSelectedSeat = {
	id: number
	amount: number
	converted_amount: number
	currency: UserCurrency
	/** Example: 13A */
	coach_number: string | null
	seat_cost: string
	/** Example: First class */
	coach_class: string | null
	number_of_adults: number | null
	vat_value_full_rate: number | null
	vat_value_half_rate: number | null
	gross_amount_for_vat_full_rate: number | null
	gross_amount_for_vat_half_rate: number | null
	/** Example: 7f8c9329-ac96-4e19-8144-eb2f91ca2322 */
	passenger_price_point_id: RailPassengerPricePointId
	/** Example: inward-TGV6106-wagon1-deck1-seat44-id */
	seat_id: RailSeatId
	leg_id: string
	/** Example: 44 */
	seat_number: string | null
	/** Example: 1QNNpUp7V20rKObsHMmPlx */
	seat_preference_group_id: RailSeatPreferenceGroupId
	/** Example: 42AlXwUSWE6BFlZBz4vKhn */
	section_id: RailSectionId
	/** Example: 68007 */
	traveler_id: UserId
	properties: string[]
}

export interface RailSegment {
	id: number
	arrival: RailItineraryDestination
	departure: RailItineraryDestination
	leg_id: string
	modified_from_bibe: boolean
	seat_selection: RailSegmentSelectedSeat[] | null
	train_number: string
	train_confirmation?: string
	loyalty_cards?: RailTravelersCardsAge
	co2_emissions: number
	/** Corresponds to leg.0.transport.designation */
	train_type: string | null
	vendor_specific_info: RailVendorSpecificInfo | null
	duration: string
	carrier: RailLeg['carrier']
	travel_class: string
	ticket_number?: string
	route: string | null
}

type RailVendorSpecificInfo = {
	platforms: {
		destination: { platform: string | null } | null
		origin: { platform: string | null } | null
	}
	transport: {
		description: string
		designation: string
		mode: RailTransportMode
	}
	ticket_number?: string
	layover_location?: string
	taxes: Tax[]
	tax_per_person: OriginalAmount
	payment_method: number | null
	payment_method_id: number
	cancellation_due_date: string
}

type RailVendorSpecificInfoSubset = Omit<RailVendorSpecificInfo, 'platforms' | 'transport'>

export interface RailSlice {
	age: null
	card_type: string
	rail_class: string | null
	rail_id: number
	segments: RailSegment[]
	passengers: number | null
	is_ticketed: boolean
	fare?: RailItineraryFare[]
	duration: string
	total_price: number
	open_return_valid_until?: string | null
	alternatives_required_for_inward_search: boolean
	legs: RailLeg[]
}

export interface ItineraryRailBooking
	extends
		WithPolicy,
		WithCancellationStatus,
		WithReservationStatus,
		WithTravelers,
		WithSearchId,
		WithBookingType,
		WithBookingSegmentToTravelers,
		WithRailExtraBookingData,
		WithPolicy,
		WithClonedFromId,
		WithCvvRequirement {
	bahn_card: string
	cancellation_penalty_before: number | null
	id: BookingSegmentId
	ticket_refundable: boolean | null
	fare_type: string
	total_price: number
	actual_price?: number
	total_tax: number
	vendor: RailVendor
	rails: RailSlice[]
	reservation_offers: RailReservationOffer[]
	selected_journeys: { rail_identifier: string; alternative_identifier: string }[]
	selected_reservation_offers_ids: RailReservationOfferId[] | null
	unselected_reservation_offers_ids: RailReservationOfferId[] | null
	seat_only?: boolean
	open_return?: boolean
	return_date?: string
	train_booking_source?: string
	external_booking_feedback?: ExternalBookingFeedback
	deutsche_bahn_credit_card_fee: string | undefined
	ticket_delivery_fee: string | undefined
	original_currency: UserCurrency
	original_total_price: number
	seat_preferences: RailSelectedSeatPreference[] | null
	seat_preference_groups: RailSeatPreferenceGroup[]
	/** Set to true when the users open Seat modal for the first time */
	seen_seat_preferences?: boolean
	payment_cards_accepted: CreditCardType[]
	applied_discount: AppliedDiscount | null
	__original_total_price?: OriginalAmount
	vendor_specific_info?: RailVendorSpecificInfoSubset
	cancellation_due_date: string
	ticket_number: string | null
	support_request_fees: SupportRequestFeeForm[]
}

export type ItineraryBooking =
	| ItineraryFlightBooking
	| ItineraryHotelBooking
	| ItineraryCarBooking
	| ItineraryRailBooking

export interface ItineraryTripFee extends TripPaymentMethodBase {
	user: User
	total_price: number
	total_tax: number
	status: WorldlineOrderStatus
	basic_price: number
}

export interface WithItineraryBookingsManagedByUserBase<FB, HB, CB, RB> {
	flights_bookings: FB
	hotels_bookings: HB
	cars_bookings: CB
	rails_bookings: RB
}

export type WithItineraryBookingsManagedByUser = WithItineraryBookingsManagedByUserBase<
	ItineraryFlightBooking[],
	ItineraryHotelBooking[],
	ItineraryCarBooking[],
	ItineraryRailBooking[]
>

export interface WithRailBookings {
	rails_bookings: ItineraryRailBooking[]
}

export type WithItineraryBookings = WithItineraryBookingsManagedByUser & WithRailBookings

export interface WithItineraryTripFeesBase<T> {
	trip_fees?: T
}

export interface VirtualPaymentAutomationFee extends ItineraryTripFee {
	booking_segment_id: BookingSegmentId
}

export interface WithVirtualPaymentAutomationFees {
	vpa_fees?: VirtualPaymentAutomationFee[]
}

export type WithItineraryTripFees = WithItineraryTripFeesBase<ItineraryTripFee[]>

export type WithItineraryServicesManagedByUser = WithItineraryBookingsManagedByUser & WithItineraryTripFees

export interface WithItineraryTravelers {
	travelers: ItineraryTravelers[]
}

export type ItineraryServicesWithTravelers = WithItineraryServicesManagedByUser & WithItineraryTravelers

export interface Itinerary extends ItineraryServicesWithTravelers, WithRailBookings, WithVirtualPaymentAutomationFees {
	notifications?: NotificationsClass
	hotel_itinerary: HotelItineraryClass
	flight_itinerary: FlightItineraryClass
	car_itinerary: CarItineraryClass
	rail_itinerary: RailItineraryClass
	surcharges: Surcharge[]
	trip_id: number
	trip_name: string
	needs_approval: boolean
	approvers: User[]
	hazardous: boolean
	errors?: ErrorsInBooking[]
	totals: {
		flight_seats: number
		rail_seats: number
		/** It is included into `rail_seats` */
		rail_selected_reservation_offers: number
		trip_cost: number
		post_pay: number
		trip_fees: number
		additional_services: number
		pay_now: number
	}
	trip_notes?: TripNote[]
}

export interface ItineraryChanges {
	flights?: PriceChange[]
	hotels?: PriceChange[]
	cars?: PriceChange[]
}

export interface PriceChange {
	oldPrice: number
	newPrice: number
	res_id?: string | null
	rate_key?: string
}

export interface Surcharge {
	charge_type: ChargeType
	description: string | null
	product_type: SurchargeProductType
	total_price: string
	total_tax: string
	booking_segment_id: number | null
	is_fully_refunded: boolean
	credit_card_id: number | null
	user_id?: number | null
}

// eslint-disable-next-line no-restricted-syntax
export enum ChargeType {
	Baggage = 'baggage',
	Meals = 'meals',
	PreferredSitting = 'preferred_sitting',
	CancellationFee = 'cancellation_fee',
	ChangingFee = 'changing_fee',
	Insurance = 'insurance',
	CarRental = 'car_rental',
	LimousineService = 'limousine_service',
	HandlingFee = 'handling_fee',
	ObFee = 'ob_fee',
	Other = 'other',
	BookingFee = 'booking_fee',
	DeliveryFee = 'delivery_fee',
	Breakfast = 'breakfast',
	/** When GetGoing charges the credit card themselves */
	CreditCardFee = 'credit_card_fee',
	/** Deutsche Bahn specific fee */
	TrainCreditCardFee = 'train_credit_card_fee',
}

interface NotificationsClass {
	failed_bookings?: WithItineraryBookings
}

interface HotelItineraryClass {
	summary: HotelSummary[]
	total_paynow: number
	total_postpay: number
	total_price: number
	total_tax: number
}

interface FlightItineraryClass {
	summary: FlightSummary[]
	total_paynow: number
	total_postpay: number
	total_price: number
	total_tax: number
}

interface CarItineraryClass {
	summary: CarSummary[]
	total_paynow: number
	total_postpay: number
	total_price: number
	total_tax: number
}

interface RailItineraryClass {
	summary: RailSummary[]
	total_paynow: number
	total_postpay: number
	total_price: number
	total_tax: number
}

export interface BookingSummary<T> {
	bs_confirmation_number: string | null
	booking_segment_id: BookingSegmentId
	travelers_ids: UserId[]
	booking_info: T[]
}

export interface HotelBookingInfo {
	hotel: HotelMinimal
	checkin: string
	checkout: string
	number_of_rooms: number
	pay_at_checkout: boolean
}

export type HotelSummary = BookingSummary<HotelBookingInfo>

interface FlightBookingInfo {
	arrival_airport: Airport
	cabin_class: keyof typeof CabinClassLabels & string
	departure_airport: Airport
	departure_date: string
	number_of_traveler: number
	pay_at_checkout: boolean
}

export interface AirlineConfirmationInfo {
	airline_confirmation_number: string | null
	airline_name: string
}

interface AirlineEticketInfo {
	airline_confirmation_numbers: AirlineConfirmationInfo[] | null
	eticket: string | null
}

export type FlightSummary = Omit<
	BookingSummary<FlightBookingInfo>,
	'bs_confirmation_number' | 'supplier_confirmation_number'
> & {
	bs_confirmation_numbers: Record<string, AirlineEticketInfo>
}

interface CarBookingInfo {
	car_type: CarType
	pickup_location: CarAgencyLocation
	dropoff_location: CarAgencyLocation | null
	pickup_date: string
	return_date: string
	prepaid: boolean
}

type CarSummary = BookingSummary<CarBookingInfo>

interface RailBookingInfo {
	departure_date: string | null
	number_of_traveler: number
	rail_class: string
	departure_station: RailDestination
	arrival_station: RailDestination
}

type RailSummary = BookingSummary<RailBookingInfo>

export interface BookedRoom {
	hotel_id: string
	rate_key: string
	room: RoomRate
}

export interface HotelMinimal extends WithName {
	id: string
	city: string
	country_code: string
	address: string
	thumbnails: string[]
	property_amenities: HotelAmenityValues[]
	room_amenities: HotelAmenityValues[]
	star_rating: HotelResult['hotel_rating']
}

export interface HotelSelectDetails extends WithSpecialRequestData {
	room: RoomRate & Partial<WithOriginalBookingSegmentId>
}

export interface TripGap extends WithTravelers {
	start_time: Date
	end_time: Date
	location: Location
	location_type: LocationTypes
}

export type HotelAmenityValues =
	| 'ACND'
	| 'AIRSH'
	| 'BAR'
	| 'BCNT'
	| 'BFST'
	| 'CCNT'
	| 'CLNG'
	| 'FBFST'
	| 'FINTT'
	| 'FPARK'
	| 'FWIFI'
	| 'GOLF'
	| 'GYM'
	| 'INTT'
	| 'KIDAC'
	| 'KTCH'
	| 'LIFT'
	| 'LNDR'
	| 'MBAR'
	| 'MTNG'
	| 'NSMK'
	| 'PARK'
	| 'PETS'
	| 'POOL'
	| 'RFRG'
	| 'RSTR'
	| 'RSVC'
	| 'SAFE'
	| 'SAUNA'
	| 'TENN'
	| 'TV'
	| 'WCHR'
	| 'WIFI'

export type HotelPaymentOptions = 'APO' | 'PAP' | 'OPP'

export const HotelAmenitiesList: HotelAmenityValues[] = [
	'ACND',
	'AIRSH',
	'BAR',
	'BCNT',
	'BFST',
	'CCNT',
	'CLNG',
	'FBFST',
	'FINTT',
	'FPARK',
	'FWIFI',
	'GOLF',
	'GYM',
	'INTT',
	'KIDAC',
	'KTCH',
	'LIFT',
	'LNDR',
	'MBAR',
	'MTNG',
	'NSMK',
	'PARK',
	'PETS',
	'POOL',
	'RFRG',
	'RSTR',
	'RSVC',
	'SAFE',
	'SAUNA',
	'TENN',
	'TV',
	'WCHR',
	'WIFI',
]

export interface HotelPriceCapRoute {
	city_name: string
	country_iso: string
	long: number
	lat: number
	max_rate: number
	radius?: number
}

// eslint-disable-next-line no-restricted-syntax
export enum ApproverSelectionType {
	Any = 'any',
	All = 'all',
}

// eslint-disable-next-line no-restricted-syntax
export enum ApprovalPolicy {
	Always = 'always',
	Never = 'never',
	WhenOutOfPolicy = 'when_out_of_policy',
}

// eslint-disable-next-line no-restricted-syntax
export enum CompanyApprovalSetting {
	Main = 'main',
	GroupApprover = 'group_approver',
	OfficeAdmin = 'office_admin',
	Both = 'both',
	Either = 'either',
}

// eslint-disable-next-line no-restricted-syntax
export enum PolicyType {
	General = 'general',
	Flight = 'flights',
	Train = 'trains',
	Hotel = 'hotels',
	PreTrip = 'pre_trip',
	Car = 'cars',
}

export function getPolicyTypeLabel({ policyType, t }: { policyType: PolicyType; t: TFunction }) {
	return {
		[PolicyType.Flight]: t('policy-types.flights'),
		[PolicyType.Hotel]: t('policy-types.hotels'),
		[PolicyType.Car]: t('policy-types.cars'),
		[PolicyType.General]: t('policy-types.general'),
		[PolicyType.PreTrip]: t('policy-types.pre-trip'),
		[PolicyType.Train]: t('Trains'),
	}[policyType]
}

// eslint-disable-next-line no-restricted-syntax
export enum DistanceUnits {
	KM = 'km',
	MI = 'miles',
}

// eslint-disable-next-line no-restricted-syntax
export enum ShortDistanceUnits {
	KM = 'KM',
	MI = 'MI',
}
export const getLongDistanceUnits: (t: TFunction) => Record<ShortDistanceUnits, string> = (t: TFunction) => ({
	[ShortDistanceUnits.KM]: t('units.Kilometers'),
	[ShortDistanceUnits.MI]: t('units.Miles'),
})

export function getPolicyGroupLabel({ groupType, t }: { groupType: GroupTypes; t: TFunction }) {
	return {
		[GroupTypes.Company]: t('policy.policy-groups.labels.company'),
		[GroupTypes.Department]: t('policy.policy-groups.labels.department'),
		[GroupTypes.Office]: t('policy.policy-groups.labels.office'),
		[GroupTypes.PolicyGroup]: t('policy.policy-groups.labels.policy-group'),
		[GroupTypes.Expense]: t('policy.policy-groups.labels.expense-policy'),
	}[groupType]
}

export function getApproverLabelByGroupTypeTranslations(
	t: TFunction,
	type: GroupTypes,
	params: { count: number } = { count: 1 },
): string {
	if (type === GroupTypes.PolicyGroup) {
		return t('policy.approver-types.trip', { count: params.count })
	}
	if (type === GroupTypes.Office) {
		return t('policy.approver-types.office', { count: params.count })
	}
	return t('policy.approver-types.company', { count: params.count })
}

/* NEW POLICY - END */

// eslint-disable-next-line no-restricted-syntax
export enum ApprovalStatus {
	WaitingApproval = 'waiting_approval',
	Approved = 'approved',
	NotApproved = 'not_approved',
	ApprovedBySupport = 'approved_by_support',
}

export const ApprovalStatusLabelMap: Record<ApprovalStatus, string> = {
	[ApprovalStatus.Approved]: 'globals.approval-status-labels.approved',
	[ApprovalStatus.ApprovedBySupport]: 'globals.approval-status-labels.approved_by-support',
	[ApprovalStatus.NotApproved]: 'globals.approval-status-labels.not-approved',
	[ApprovalStatus.WaitingApproval]: 'globals.approval-status-labels.waiting-approval',
}

export interface TripApproval extends WithUserId {
	request_message: string
	message: string
	rejection_reason_type: SurchargeProductType
	approval_status: ApprovalStatus
	user: User
	created_dt: string
}

/** Used in approvals. Logically is BookingSegmentProductType, but with an additional value `all`. Is a separate enum on BE. */
// eslint-disable-next-line no-restricted-syntax
export enum SurchargeProductType {
	All = 'all',
	Flights = 'flights',
	Hotels = 'hotels',
	Cars = 'cars',
	Rails = 'rails',
}

// eslint-disable-next-line no-restricted-syntax
export enum FlightBookingStatuses {
	Failed = 'failed',
	Booked = 'booked',
	Waiting = 'pending',
}

// eslint-disable-next-line no-restricted-syntax
export enum CarBookingStatuses {
	Failed = 'failed',
	Booked = 'booked',
	Waiting = 'pending',
}

export interface PriceChangesData {
	total_price: number
	total_tax: number
	old_price: number
	old_tax: number
}

export interface ItineraryRefresh {
	id: number
	status: 'created' | 'sent' | 'success' | 'failed' | 'terms_changed'
}

export interface ReportsOverviewData {
	period: string
	overview: Overview
	flights: ReportsOverviewFlights
	hotels: ReportsOverviewHotels
	cars: ReportsOverviewCars
	rails: ReportsOverviewRails
	travelers: ReportsOverviewTravelers
}

export interface OptionWithId extends WithName {
	id: number
}

export type ReportingFieldFilterValue = string | null

export interface ReportsMetaDataBase {
	countries: OptionWithId[]
	invoice_profiles: OptionWithId[]
	departments: ReportingFieldFilterValue[]
	offices: ReportingFieldFilterValue[]
}

export interface ReportsMetaData extends ReportsMetaDataBase {
	accounting_unit: ReportingFieldFilterValue[]
	action_code: ReportingFieldFilterValue[]
	billable: ReportingFieldFilterValue[]
	cost_center: ReportingFieldFilterValue[]
	internal_account: ReportingFieldFilterValue[]
	project_code: ReportingFieldFilterValue[]
	purchase_order_number: ReportingFieldFilterValue[]
	employee_id: ReportingFieldFilterValue[]
	out_of_policy_reason: ReportingFieldFilterValue[]
	custom_field_1: ReportingFieldFilterValue[]
	custom_field_2: ReportingFieldFilterValue[]
	custom_field_3: ReportingFieldFilterValue[]
	trip_purpose: string[]
}

export interface ReportsOverviewFlights extends WithReportFlightCabinClass {
	total_airfare: number
	fare_type: FareType
	direct_flights: ValueTotal
	flexible: ValueTotal
	flights_per_trip_type: FlightsPerTripTypeElement[]
}

export type RailCabinClassValueTotal = {
	first_train: number
	second_train: number
	total: number
}

export type RailFareTypeStats = {
	tickets: number
	seats: number
	total: number
}

export type ReportsOverviewRails = {
	cabin_class: RailCabinClassValueTotal
	fare_type: RailFareTypeStats
	trips_per_type: RailsPerTripTypeElement[]
	total_rail_spend: number
}

export type RailsPerTripTypeElement = {
	key: string
	avg_ticket_price: number
	booked_in_advance: number
	cabin_class: RailCabinClassValueTotal
	num_travelers: number
	num_trips: number
	total_rail_spend: number
	trips_by_path?: RailsPerTripTypeElement[]
}

export type SpecificRailsPerTripTypeElement = Omit<RailsPerTripTypeElement, 'trips_by_path'> & {
	end_date: string
	open_return_valid_until?: string | null
	start_date: string
	trip_id: number
}

export interface ReportsOverviewTravelers {
	booked_by_traveler: ValueTotal
	booked_in_advance: number
	per_traveler: PerTravelerStats[]
	rejected: ValueTotal
	sent_for_approval: ValueTotal
	travelers_count: number
	trips_count: number
}
export interface ReportsFlightCabinClassValueTotal {
	economy: number
	business: number
	first: number
	premium_economy: number
	total: number
}

export interface WithReportFlightCabinClass {
	cabin_class: ReportsFlightCabinClassValueTotal
}

export type WithReportRailCabinClass = {
	cabin_class: RailCabinClassValueTotal
}

export type WithReportCabinClass = WithReportFlightCabinClass | WithReportRailCabinClass

export type ReportCabinClassValueTotal = ReportsFlightCabinClassValueTotal | RailCabinClassValueTotal

export type ReportCabinClassKey = keyof ReportsFlightCabinClassValueTotal | keyof RailCabinClassValueTotal

interface ValueTotal {
	value: number
	total: number
}

interface Breakfast {
	free: number
	payed: number
	total: number
}

interface FareType {
	tickets: number
	total: number
}

// eslint-disable-next-line no-restricted-syntax
export enum HaveChanges {
	none = 'none',
	changed = 'changed',
	cancelled = 'cancelled',
}

export function getTripChangesLabel(haveChanges: HaveChanges | string, t: TFunction) {
	return (
		{
			[HaveChanges.none]: t('trip-changes.none'),
			[HaveChanges.changed]: t('trip-changes.changed'),
			[HaveChanges.cancelled]: t('trip-changes.cancelled'),
		}[haveChanges] || capitalize(haveChanges)
	)
}

export interface FlightsPerTripTypeElement extends WithReportFlightCabinClass {
	key: string
	emissions_total: number
	num_trips: number
	total_airfare: number
	num_travelers: number
	avg_ticket_price: number
	refund_amount: number
	direct_flights: ValueTotal
	booked_in_advance: number
	flexible: ValueTotal
	flights_by_path?: FlightsPerTripTypeElement[]
	have_changes: HaveChanges
	status?: string
	changes: ValueTotal
	change_or_cancel_fees: number
}

export interface SpecificFlightsPerTripTypeElement extends FlightsPerTripTypeElement {
	start_date: string
	end_date: string
	flights_by_path: never
}

export interface PerTravelerStats {
	cabin_class: ReportCabinClassValueTotal
	key: number
	traveler: { full_name: string; image_url: string }
	num_nights: number
	total_spend: number
	trips_count: number
	flexible: ValueTotal
	booked_in_advance: number
	avg_ticket_price: number
	avg_night_price: number
	booked_by_traveler: ValueTotal
	have_changes: HaveChanges
	changes: ValueTotal
	change_or_cancel_fees: number
}

// Generated by https://quicktype.io

export interface PerTravelerTrip extends WithName {
	key: string
	hotels: TravelerTripHotel[]
	total_spend: number
	flights: TravelerTripFlight[]
	rails: TravelerTripRail[]
	cars: TravelerTripCar[]
	change_or_cancel_fees: number
	have_changes: string
	booked_in_advance: number
	booked_by_traveler: TravelerTripsBookedByTraveler
	flexible: TravelerTripsBookedByTraveler
	start_date: string
	end_date: string
}

interface TravelerTripsBookedByTraveler {
	total: number
	value: number
}

interface TravelerTripFlight extends WithName, WithReportFlightCabinClass {
	total_spend: number
	change_or_cancel_fees: number
	have_changes: string
	booked_in_advance: number
	booked_by_traveler: FlightBookedByTraveler
	avg_ticket_price: number
	flexible: FlightBookedByTraveler
}

export type TravelerTripRail = WithName & {
	avg_ticket_price: number
	booked_by_traveler: ValueTotal
	booked_in_advance: number
	cabin_class: RailCabinClassValueTotal
	change_or_cancel_fees: 0
	flexible: { value: 0; total: 1 }
	have_changes: 'none'
	total_spend: number
}

interface TravelerTripCar extends WithName {
	booked_by_traveler: FlightBookedByTraveler
	booked_in_advance: number
	change_or_cancel_fees: number
	flexible: FlightBookedByTraveler
	have_changes: string
	total_spend: number
	num_days: number
}

interface FlightBookedByTraveler {
	value: number
	total: number
}
interface TravelerTripHotel extends WithName {
	total_spend: number
	change_or_cancel_fees: number
	have_changes: string
	avg_rating: number
	booked_in_advance: number
	booked_by_traveler: FlightBookedByTraveler
	num_nights: number
	avg_night_price: number
	flexible: FlightBookedByTraveler
}

export interface ReportsOverviewHotels {
	total_hotel_fare: number
	num_nights: number
	num_hotels: number
	avg_nights: number
	avg_rating: number | null
	avg_night_price: number | null
	refundable: ValueTotal
	breakfast: Breakfast
	hotels_per_city: HotelsPerCity[]
}

export interface HotelsPerCity {
	key: string
	num_trips: number
	total_hotel_fare: number
	num_travelers: number
	num_nights: number
	avg_nights: number
	avg_night_price: number
	avg_rating: number | null
	booked_in_advance: number
	num_hotels: number
	refundable: ValueTotal
	breakfast: Breakfast
	have_changes: HaveChanges
	changes: ValueTotal
	change_or_cancel_fees: number
}

export interface HotelsPerCityDetails extends HotelsPerCity {
	city_with_country: string
	per_trip: HotelsPerTrip[]
	hotel_name: string
}

interface HotelsPerTrip extends HotelsPerCity {
	start_date: string
	end_date: string
}

interface CarPerTrip extends CarPerCity {
	start_date: string
	end_date: string
}

export interface CarsPerCityDetails extends CarPerCity {
	country: string
	city_or_airport: string
	rent_agency: string
	per_trip: CarPerTrip[]
	display_name: string
}

export interface ReportsOverviewCars {
	total_car_spend: number
	num_days: number
	num_cars: number
	avg_days: number
	avg_rate: number | null
	refundable: ValueTotal
	cars_per_city: CarPerCity[]
}

export interface CarPerCity {
	key: string
	num_trips: number
	total_car_spend: number
	num_days: number
	avg_days: number
	avg_rate: number
	num_cars: number
	refundable: ValueTotal
	have_changes: HaveChanges
	booked_in_advance: number
	changes: ValueTotal
	change_or_cancel_fees: number
	rent_agency: string
}

interface ExpiringTicketsBreakdownGroup {
	count: number
	total: number
}

export interface ExpiringTicketsBreakdown {
	less_than_one_month: ExpiringTicketsBreakdownGroup
	one_to_three_months: ExpiringTicketsBreakdownGroup
	three_to_six_months: ExpiringTicketsBreakdownGroup
	more_than_six_months: ExpiringTicketsBreakdownGroup
}

export interface TrendItem {
	current_value?: number
	previous_value?: number
	percentage?: number
	direction?: 'up' | 'down'
	formatted_percentage?: string
	comparison_label?: string
}

export interface OverviewTrends {
	total_spend?: TrendItem
	total_airfare?: TrendItem
	total_rail_spend?: TrendItem
	total_hotel_fare?: TrendItem
	total_car_spend?: TrendItem
}

export interface UnusedTicketsOverview {
	expiring_tickets_breakdown: ExpiringTicketsBreakdown
	active: ExpiringTicketsBreakdownGroup
	expired: ExpiringTicketsBreakdownGroup
	exchanged: ExpiringTicketsBreakdownGroup
}

export interface Overview extends UnusedTicketsOverview {
	emissions_flights: number
	total_airfare: number
	total_hotel_fare: number
	total_car_spend: number
	total_spend: number
	total_rail_spend: number
	trends?: OverviewTrends
	trips_count: number
	travelers_count: number
	destinations_count: number
	in_policy: ValueTotal
	with_pre_trip: ValueTotal
	cancelled_trips: ValueTotal
	sent_for_approval: ValueTotal
	rejected: ValueTotal
	time_plan_to_submit: number | null
	time_submit_to_approval: number | null
	num_flights: number
	miles_flown: number
	num_hotels: number
	num_cars: number
	num_nights: number
	num_car_days: number
	num_rails: number
	avg_rating: number | null
	top_travelers: TopTravelers
	top_countries: TopCountries
	pre_trips: PreTripReport
	purpose: TripPurposeCounter[]
	purpose_list: TripPurposeOption[]
}

interface PreTripReport {
	approved_pre_trip_requests: number
	total_pre_trip_requests: number
	pre_trips_enabled: boolean
}

type TopCountries = {
	[k in keyof typeof CountriesCode]: number
} & {
	total: number
}

interface TopTravelers {
	total: number
	[k: string]: number
}

export function getSurchargeLabel(charge: Surcharge, t: TFunction, params?: { productType: string }): string {
	const map: Record<Surcharge['charge_type'], string> = {
		baggage: t('flights.surcharge-labels.baggage'),
		cancellation_fee: t('flights.surcharge-labels.cancellation-fee', params),
		car_rental: t('flights.surcharge-labels.car-rental'),
		changing_fee:
			Number(charge.total_price) >= 0
				? t('flights.surcharge-labels.changing-fee', params)
				: t('flights.surcharge-labels.changing-fee-refund', params),
		handling_fee: t('flights.surcharge-labels.handling-fee'),
		insurance: t('flights.surcharge-labels.insurance'),
		limousine_service: t('flights.surcharge-labels.limousine-service'),
		meals: t('flights.surcharge-labels.meals'),
		other: t('flights.surcharge-labels.other'),
		preferred_sitting: t('flights.surcharge-labels.preferred-sitting'),
		ob_fee: t('flights.surcharge-labels.ob-fee'),
		booking_fee: t('Train booking fee'),
		train_credit_card_fee: t('Train credit card fee'),
		delivery_fee: t('Train delivery fee'),
		credit_card_fee: t('Payment Service Fee'),
		breakfast: t('surcharge-labels.breakfast'),
	}

	return map[charge.charge_type] || capitalize(charge.charge_type.replace('_', ' '))
}

export const getCleanPhoneNumber = (phoneNumber: string) => phoneNumber.replace(/[^0-9+]/g, '')

export const HEADER_HEIGHT = 100
export const MOBILE_HEADER_HEIGHT = 50

export const WEBSITE_URL_BY_LOCALE: Record<Locales, string> = {
	[Locales.enUS]: `https://${WEBSITE_BASE}`,
	[Locales.enGB]: `https://${WEBSITE_BASE}`,
	[Locales.deDE]: `https://${WEBSITE_BASE}/de`,
	[Locales.frFR]: `https://${WEBSITE_BASE}`,
	[Locales.nlNL]: `https://${WEBSITE_BASE}`,
}

export const DETAILED_PRICING_URL_BY_LOCALE: Record<Locales, string> = {
	[Locales.enUS]: `${WEBSITE_URL_BY_LOCALE[Locales.enUS]}/pricing/#allfeatures`,
	[Locales.enGB]: `${WEBSITE_URL_BY_LOCALE[Locales.enGB]}/pricing/#allfeatures`,
	[Locales.deDE]: `${WEBSITE_URL_BY_LOCALE[Locales.deDE]}/preise/#allfeatures`,
	[Locales.frFR]: `${WEBSITE_URL_BY_LOCALE[Locales.frFR]}/pricing/#allfeatures`,
	[Locales.nlNL]: `${WEBSITE_URL_BY_LOCALE[Locales.nlNL]}/pricing/#allfeatures`,
}

// eslint-disable-next-line no-restricted-syntax
enum SortDirection {
	Asc,
	Desc,
}

export { SortDirection }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface SortDef<T = any> {
	key: T
	direction: SortDirection
}

export interface ResultSegment {
	key: string
	label: string
	itemsLength?: number
	alwaysExpanded?: boolean
	icon?: React.ReactNode
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	filterFn(item: any): boolean | null
}

export interface SeatMapCollection {
	seg_seq_num: number
	departure: string
	arrival: string
	seat_map: SeatMap
	status: SeatPollingStatus
	user_ids: number[]
	booking_segment_id: number
}

interface SeatMap {
	cabins: Cabin[]
}

interface Cabin {
	location: string
	column_names: CabinColumnName[]
	rows: CabinRow[]
	wing: WingDetails
}

// eslint-disable-next-line no-restricted-syntax
enum CabinColumnName {
	Empty = ' ',
	A = 'A',
	B = 'B',
	C = 'C',
	D = 'D',
	E = 'E',
	F = 'F',
	G = 'G',
	H = 'H',
	I = 'I',
	J = 'J',
	L = 'L',
	M = 'M',
}

interface CabinRow {
	row_number: number | null
	is_exit: boolean
	is_wing: boolean
	cells: CabinCell[]
}

export interface CabinCell extends WithPolicy {
	cell_type: CabinCellType
	// raw_data: RawData | null
	cell_name: null | string
	seat_status: SeatStatus | null
	seat_extra_cost: number | null
	seat_premium: boolean | null
}

// eslint-disable-next-line no-restricted-syntax
enum CabinCellType {
	Aisle = 'AISLE',
	Galley = 'GALLEY',
	Lavatory = 'LAVATORY',
	None = 'NONE',
	Seat = 'SEAT',
}

// eslint-disable-next-line no-restricted-syntax
enum SeatStatus {
	Vacant = 'AVAILABLE',
	Occupied = 'OCCUPIED',
	Blocked = 'BLOCKED',
}

interface WingDetails {
	first_row: number
	last_row: number
}

// eslint-disable-next-line no-restricted-syntax
enum SeatPollingStatus {
	Pending = 'IN_PROCESS',
	Success = 'SUCCESS',
	Fail = 'FAILED',
}

/**
 * This class mirrors [React.Component], except it adds [muiName: string] public static variable
 * @see React.Component
 */
export class MUIComponent<P = {}, S = {}> extends React.Component<P, S> {
	public static muiName: string
}

/**
 * This type mirrors [React.FunctionComponent], except it adds [muiName: string] property
 * @see React.FunctionComponent
 */
export type MUIFunctionComponent<P = {}> = React.FunctionComponent<P> & { muiName: string }

export type ElementEvent<C = Element, T = Event, E = EventTarget> = React.BaseSyntheticEvent<T, E & C, C>

export interface PackageUsageData {
	amount_of_paid_support_request_fees?: number
	amount_of_paid_trip_fees?: number
	amount_of_paid_user_fees?: number
	amount_of_paid_vpa_fees?: number
	country_code: CountriesCode
	country_currency: UserCurrency
	country_name: string
	free_support_request_amount_one_time?: number | null
	free_support_request_amount_time_period?: number | null
	free_support_request_renewal_date?: string | null
	free_trip_amount_one_time?: number | null
	free_trip_amount_time_period?: number | null
	free_trip_renewal_date?: string | null
	free_user_amount_one_time?: number | null
	free_user_amount_time_period?: number | null
	free_user_renewal_date?: string | null
	package_commercial_name: string
	renewal_date: string
	start_date: string
	support_request_fee_price?: number
	support_request_fee_unlimited: boolean
	total_amount_of_basic_fees: number
	total_amount_of_total_fees: number
	total_amount_of_vats: number
	trip_fee_price?: number
	trip_fee_unlimited: boolean
	user_fee_price?: number
	user_fee_unlimited: boolean
	vpa_fee_price: number
	vpa_fee_unlimited: boolean
	users_type?: 'active' | 'profiled'
	company_subscription_fee: number | null
	billed_annually: boolean | null
	complimentary_period_message: string
}

export interface LiveUserLocation {
	trip_id: number
	traveler: User
	location: Location
	location_name: string
	verified: boolean
	user_tracking_id: number
}

export interface UserTrackingQS {
	user_tracking_id: number
	status: 'confirmed' | 'declined'
}

export interface CompanyPreference {
	preference_type: CompanyPreferenceType
	enabled: boolean
	description: string
	excluded_users: User[]
	included_users: User[]
}

export interface CompanyPreferenceUpdateData {
	preference_type: CompanyPreferenceType
	enabled: boolean
	add_included_users: number[]
	remove_included_users: number[]
	add_excluded_users: number[]
	remove_excluded_users: number[]
}

export interface HomePageData {
	is_trip_approver: boolean
	is_pre_trip_green_light_mode: boolean
	waiting_to_approve: PreTripRequest[]
	expense_reports_waiting_to_approve: ExpenseReportResponse[]
	pre_trip_requests: PreTripRequest[]
	number_of_waiting_trip_approval: number
	total_number_of_pre_trip_approval: number
	total_number_of_trip_approval: number
	has_missing_profile_details: boolean
	number_of_waiting_for_user_submission: number
	is_passport_expired: boolean
	is_national_ID_expired: boolean
	number_of_failed_fees: number
}

export interface PreTripRequest extends WithTravelers, WithName {
	id: number
	route: PreTripRoute[]
	purpose: TripPurpose[]
	notes: string
	pre_trip_approvals: PreTripApproval[]
	status: ApprovalStatus
	created_dt: string
}

interface PreTripApproval {
	approver: User
	approval_status: ApprovalStatus
	approval_status_dt: string
	message: string | null
}

export interface PreTripRequestUpdateData {
	name?: string
	route: PreTripRoute[]
	purpose: TripPurpose[]
	notes: string
	travelers_ids: UserId[]
}

export interface PreTripRequestManageData {
	message: string
	status: ApprovalStatus
}

export interface PreTripRoute {
	from_location_id: LocationId
	to_location_id: LocationId
	from_location: Location
	to_location: Location
	from_date: string
	to_date: string
}

export interface CompanyTypeModal {
	showModal: boolean
}

// eslint-disable-next-line no-restricted-syntax
export enum SafetyLabels {
	LiveMap = 'live-map',
}

export interface ItineraryFareData {
	id: number
	fare_group_key?: FlightFareGroupKey
	fare_options_keys?: FlightOptionKey[]
}

type SeatMapTab = number | null
export interface ItinerarySeatMapState {
	selectedSeats: SeatMapSegmentsSelection | {}
	seatMapOpen: boolean
	seatMapTab: SeatMapTab
	flightSeatSelection: SeatMapFlightResultSelection | {}
	seat: SeatMapSeat | undefined | null
	flightOptionKeys: ItineraryFareData[]
	flightSegments: Record<string, MinimalSegment[][]> | {}
	res_id: Record<number, string> | {}
	seatMapSelection: SeatMapSegmentsSelection | null
	emergencyRowSelections: SeatMapSegmentsSelection | {}
	showDeleteSeatConfirmation: boolean
	isMultipleFlight: boolean
	selectedFlightId: number
}

export type CancellationFlightOrHotelDetails = SupportRequest[]

export interface CustomerSuccessConsultant {
	first_name: string
	middle_name: string | null
	last_name: string
	phone: string | null
	email: string | null
	hubspot_id: string | null
	photo_url: string | null
}

export interface UserPOS {
	id: number
	email: string | null
	whatsapp: string | null
	telegram: string | null
	contact_phone: string | null
	terms_of_services_de: string | null
	terms_of_services_us: string | null
	terms_of_services_uk: string | null
	terms_of_services_fr: string | null
	terms_of_services_nl: string | null
	operational_manager_email: string | null
	country: POSCountry
	official_currency: UserCurrency
	units: ShortDistanceUnits
	agency_legal_entity_name: string | null
	agency_legal_entity_trade_register_number: string | null
	agency_tax_number: string | null
	agency_legal_entity_address: string | null
	agency_sap_company_code: string | null
	agency_iata_arc_number: string | null
	standard_high_tax_rate: number | null
}

export type CountryPOSStatus = 'active' | 'inactive'

export type CountryPOSRegion = 'Europe' | 'Americas' | 'APAC'

export type POSType = 'PRIMARY' | 'VIRTUAL'

export interface CountryPOS {
	id: number
	status: CountryPOSStatus
	agency_additional_information: string[]
	agency_iata_arc_number: string
	agency_legal_entity_address: string
	agency_legal_entity_name: string
	agency_legal_entity_trade_register_number: string
	agency_sap_company_code: string
	agency_tax_number: string | null
	agency_travel_agent_registration_number: string | null
	contact_phone: string
	country: POSCountry
	default_asb_entity_id: string
	default_language: string
	email: string
	invoice_profiles_count: number
	official_currency: UserCurrency
	operational_manager_email: string | null
	region: CountryPOSRegion
	standard_high_tax_rate: number
	telegram: string | null
	terms_of_services_de: string
	terms_of_services_uk: string
	terms_of_services_us: string
	travel_hub_merchant_id: string | null
	whatsapp: string | null
	units: ShortDistanceUnits
	primary_pos_id: number | null
	pos_type: POSType
	vpa_fee: number
	converted_vpa_fee: number
	package_default_settings: { package_id: number; pricing_model_id: number; billed_annually: boolean }[]
}

// eslint-disable-next-line no-restricted-syntax
export enum SearchType {
	Flights = 'flights',
	Rails = 'rails',
	Mixed = 'mixed',
}

export type SearchVariant =
	| 'home' // Full form
	| 'sidebar'
	| 'peek' // Like the home, but shows only direction inputs initially

export interface ISearchVariant {
	variant?: SearchVariant
}

// eslint-disable-next-line no-restricted-syntax
export enum TripPurpose {
	ClientMeeting = 'client_meeting',
	ExternalConference = 'external_conference',
	InstallationMeeting = 'installation_meeting',
	InternalEvent = 'internal_event',
	InternalMeeting = 'internal_meeting',
	/** This is a special value used only in the report counter */
	NoPurpose = 'no_reason',
	OffsiteProject = 'offsite_project',
	Other = 'other',
	Recruitment = 'recruitment',
	SalesMeeting = 'sales_meeting',
	SupplierMeeting = 'supplier_meeting',
	Training = 'training',
}

export interface TripPurposeOption {
	value: TripPurpose
	label: string
}

export interface TripPurposeCounter {
	key: string
	count: number
}

export interface EssentialTravelInformation {
	title: string
	short_description: string
	description: string
}

export const BRAND_SPECIFIC = {
	SMART_PRICE_POLICY: 'GoSmart',
}

export interface Office extends WithName {
	address: string
	city_name: string
	contact_email?: null
	contact_name?: null
	contact_phone?: null
	country_code: string
	created_dt: string
	description?: null
	group_id: number
	has_policy: boolean
	id: number
	latitude: number
	longitude: number
	office_approver?: null
	office_type: string
	state_name: string
}

export interface TravelerDocuments {
	national_ids: NationalId[] | []
	passports: Passport[] | []
}

export type NationalIdWithDocumentType = NationalId & { documentType: TravelerDocumentType }
export type PassportWithDocumentType = Passport & { documentType: TravelerDocumentType }
export type TravelerDocumentsWithType = NationalIdWithDocumentType | PassportWithDocumentType
export interface SelectedDocuments extends WithUserId<UserId | null> {
	document_id: number | null
	document_type: TravelerDocumentType
}
export type ExtendedNationalId = NationalIdWithDocumentType & { userId: UserId | null }
export type ExtendedPassport = PassportWithDocumentType & { userId: UserId | null }

// eslint-disable-next-line no-restricted-syntax
export enum DynamicFormNodeType {
	Text = 'text',
	Select = 'select',
	AnyOf = 'any_of',
	Group = 'group',
}

export interface DynamicFormNodeBase {
	type: DynamicFormNodeType
	e2e: string
	hint: string | null
	info: string | null
	label: string | null
	title: string | null
	title_text: string | null
}

export interface DynamicFormFieldBaseAttributes extends WithName {
	mandatory: boolean
}

export type DynamicFormFieldBase = DynamicFormNodeBase & DynamicFormFieldBaseAttributes

export interface DynamicFormField extends DynamicFormFieldBase {
	id: number
	default_value: string | null
	disabled_reason?: string
}

export interface DynamicFormTextField extends DynamicFormField {
	min_length: number | null
	max_length: number | null
	last_two_digits: boolean
	first_three_letters: boolean
}

export interface DynamicFormSelectOption {
	label: string
	value: string
}

export interface DynamicFormSelectField extends DynamicFormField {
	options: DynamicFormSelectOption[]
}

export interface DynamicFormFieldsGroup extends DynamicFormNodeBase {
	fields: (DynamicFormTextField | DynamicFormSelectField)[]
}

export type DynamicFormAnyOfField = DynamicFormFieldsGroup & DynamicFormFieldBaseAttributes

export type DynamicFormDefinition = (
	| DynamicFormAnyOfField
	| DynamicFormSelectField
	| DynamicFormTextField
	| DynamicFormFieldsGroup
)[]

export interface RewardProgramLevel {
	id: number
	value: string
	key: string
	hint: string
	info: string
}

export interface FlightRewardProgramFormField extends DynamicFormTextField {
	reward_program_level: RewardProgramLevel | null
}

export interface FlightRewardProgramFieldGroup extends WithName {
	label: string | null
	id: number
	hint: string | null
	info: string | null
	flight_program_form_fields_list: FlightRewardProgramFormField[]
	one_of_fields_is_required: boolean
	title: string | null
	title_text: string | null
}

export interface AirlineRewardProgram {
	airline_display: {
		iata: FlightCarrierCode
		id: number
	} & WithName
	id: number
	reward_program: {
		discount_type: string
		id: number
		individual_membership_required: boolean
		program_levels: RewardProgramLevel[]
	} & WithName
	is_program_exists: boolean
	disabled_reason: string | null
}

interface CompanyFlightRewardField {
	field_value: string
	reward_field: FlightRewardProgramFormField
	id: number
}
export interface CompanyFlightProgram {
	id: number
	airline_reward: AirlineRewardProgram
	fields: CompanyFlightRewardField[]
	reward_program_level: RewardProgramLevel | null
}

interface CompanyFlightProgramFieldApiDataBase {
	airline_reward_id: number
	field_value: string
}

interface WithRewardProgramLevel {
	reward_program_level_id?: number
}

interface WithFlightRewardProgramLevel {
	flight_reward_program_field_id: number
}

export type CompanyFlightProgramFieldApiData = CompanyFlightProgramFieldApiDataBase &
	WithRewardProgramLevel &
	WithFlightRewardProgramLevel

export type CompanyFlightProgramCreationFormData = CompanyFlightProgramFieldApiData[]

export interface CompanyFlightProgramUpdateFormData {
	company_reward_program_id: number
	fields: (
		| (CompanyFlightProgramFieldApiDataBase & {
				company_reward_program_field_id: number
		  })
		| (CompanyFlightProgramFieldApiDataBase & WithFlightRewardProgramLevel)
	)[]
	reward_program_level_id?: number
}

export interface WithSabreRateAccessCode {
	sabre_rate_access_code: string
}

export interface CompanyHotelProgram extends WithSabreRateAccessCode {
	id: number
	display_name: string
	description: string | null
}

export type CompanyHotelProgramCreationData = Omit<CompanyHotelProgram, 'id'>

export interface CompanyCarProgram {
	id: number
	corporate_discount_code: string
	car_loyalty_program: CarLoyaltyProgram
	direct_billing_agreement: string | null
}

export interface CompanyCarProgramCreationData {
	car_loyalty_program_code: string
	corporate_discount_code: string
	direct_billing_agreement: string | null
}

export type NamedIdentifiable = PersonNames & Identifiable

// eslint-disable-next-line no-restricted-syntax
export enum FareAvailability {
	Available = 'available',
	NotEnoughSeats = 'not_enough_seats',
	NotAvailable = 'not_available',
}

export interface BookingSegmentAvailability {
	fare_availability: FareAvailability
	booking_segment_id: number
}

export interface ServicesAvailabilityResponse {
	status: 'sent' | 'success'
	fare_availabilities: BookingSegmentAvailability[]
}

export type HotelAvailabilityResult = {
	status: string
	result: { price_metadata: PricesMetadata; results: HotelResult[] }
}

export interface BookingSegmentToTravelerFakeBookingSetting {
	booking_segment_to_travelers_id: BSTTId
	mock: string
}

export interface TripFakeBookingSettings {
	mocks: BookingSegmentToTravelerFakeBookingSetting[]
}

/** @deprecated */
export interface WithJobStatus {
	status: JobStatus
}

/** @deprecated */
export interface WithResult<T> {
	result: T
}

/** @deprecated */
export type JobResult<T> = WithJobStatus &
	WithResult<T | null> & {
		errors: string[]
	}

export interface FlightFaresResult {
	fare_groups: FlightResult[]
}

export interface HotelFaresResult {
	rates: RoomRate[]
}

export type AlternativeFlightData = FlightResult<Flight>

export type AlternativeHotelData = { rate: RoomRate } | { hotel: HotelResult }

export type WithPaymentMethods = WithCreditCards & WithVirtualPaymentAutomations

export type UserPaymentMethods = WithUserId & WithPaymentMethods

export type CardCvcData = Record<string, string>

export interface WithCardCvcData {
	card_cvc_data: CardCvcData
}

export interface WithFeePaymentMethod {
	fees?: WithPaymentMethodId | null
}

export type TravelerPaymentMethod = WithUserId & WithPaymentMethodId

export type ServicePaymentMethods = Partial<{
	flights: WithPaymentMethodId | null
	cars: WithPaymentMethodId | null
	hotels: WithPaymentMethodId | null
	rails: WithPaymentMethodId | null
}>

export type ServicePaymentMethodsWithFeeCard = WithFeePaymentMethod & ServicePaymentMethods

export type TravelerServicePaymentMethods = ServicePaymentMethodsWithFeeCard & WithUserId

export interface WithMultipleCreditCards<T> {
	multiple_credit_cards: T[]
}

export type SeparateCardPayload = TravelerPaymentMethod | TravelerServicePaymentMethods

export type MultipleCreditCardsPayload = WithMultipleCreditCards<SeparateCardPayload>

export type TripPaymentMethodConfigPayload = MultipleCreditCardsPayload | WithPaymentMethodId

// eslint-disable-next-line no-restricted-syntax
export enum FareSearchOption {
	AllResults = 'all',
	FullyFlexible = 'flexible',
	Changeable = 'changeable',
	Refundable = 'refundable',
	ChangeableAndRefundable = 'changeable and refundable',
}

export const TripNoteTypes = ['PRIVATE', 'PUBLIC', 'SYSTEM'] as const

export type TripNote = {
	is_agent_only: boolean
	note: string
	last_modified_dt: string
	created_by_user: PersonNames & Pick<User, 'email' | 'id' | 'image_url'>
	trip_id: number
	id: number
	type: (typeof TripNoteTypes)[number]
	created_dt: string
}

export type TimeOptions = 'past-3-months' | 'current-year' | 'last-year' | 'past-month' | 'past-year' | 'all-time'

export interface TimePeriodDetailed<T = string> {
	start_date: T
	end_date: T
}
